import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Activity, RefreshCw, AlertCircle, Search, Calendar, Filter, ChevronRight, ChevronDown, Play, Pause, Phone } from 'lucide-react';
import { getCurrentUser } from '../services/auth';

// ───────────────────────────────────────────────────────────────────────────
// Style primitives
// ───────────────────────────────────────────────────────────────────────────
const TH = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)', position: 'sticky', top: 0, whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', fontSize: 12.5, borderBottom: '1px solid var(--gray-100)', color: 'var(--gray-800)', verticalAlign: 'top' };
const INPUT = { padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };
const BTN = (bg, disabled) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: disabled ? 'var(--gray-300)' : bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' });
const PILL = (active) => ({ padding: '5px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (active ? 'var(--navy)' : 'var(--gray-300)'), background: active ? 'var(--navy)' : '#fff', color: active ? '#fff' : 'var(--gray-700)', whiteSpace: 'nowrap' });
const CODE = { fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontSize: 11.5, background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4, padding: '10px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'var(--gray-800)', overflow: 'auto', maxHeight: 280 };
const CARD = { background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' };

const LEVEL_BADGE = {
  ERROR: { bg: '#fde6e6', fg: '#a51d1d', dot: '#f85149' },
  WARN:  { bg: '#fff4e0', fg: '#a06000', dot: '#d29922' },
  OK:    { bg: '#e6f4ea', fg: '#1a8a4a', dot: '#3fb950' },
  INFO:  { bg: '#e6f1fa', fg: '#1268a8', dot: '#58a6ff' },
};

const TILE_COLORS = {
  green: { bg: '#e6f4ea', fg: '#1a8a4a', border: '#1a8a4a' },
  yellow: { bg: '#fff4e0', fg: '#a06000', border: '#d29922' },
  red: { bg: '#fde6e6', fg: '#a51d1d', border: '#f85149' },
  blue: { bg: '#e6f1fa', fg: '#1268a8', border: '#58a6ff' },
  muted: { bg: 'var(--gray-50)', fg: 'var(--gray-700)', border: 'var(--gray-300)' },
};

// ───────────────────────────────────────────────────────────────────────────
// Event semantics — ported from CheckoutMonitor TelemetryService.cs
// (handoff_checkout_monitor_to_cpx_prototype.md). Keep in sync as new
// events ship.
// ───────────────────────────────────────────────────────────────────────────

// Friendly names for the event detail pane. Fallback humanizes the LAST
// TWO dotted segments — never just one (single-segment fallback was rejected
// because it produced unhelpful labels like "Error" or "Entered").
const FRIENDLY_NAMES = {
  'Payment.handleSubmit.entered': 'Place Order clicked',
  'Payment.handleSubmit.submitOrder.error': 'Order submission failed',
  'Payment.handleSubmit.submitOrder.success': 'Order submitted',
  'Payment.handleSubmit.submitOrder.before': 'Order submit started',
  'Payment.handleSubmit.submitOrder.after': 'Order submit completed',
  'Payment.handleSubmit.branching': 'Payment branching',
  'Payment.handleSubmit.customSubmit.invoking': 'Custom submit invoked',
  'order.submit.success': 'Order completed',
  'order.preSubmit.snapshot': 'Pre-submit state captured',
  'order.preSubmit.snapshotError': 'Pre-submit snapshot failed',
  'order.submit.telemetryError': 'Order telemetry error',
  'checkout.unhandledError': 'Unhandled checkout error',
  'updateSelectedOption.invoke': 'Shipping option clicked',
  'updateSelectedOption.aborted': 'Shipping click aborted (race guard)',
  'updateSelectedOption.success': 'Shipping option updated',
  'updateSelectedOption.error': 'Shipping option update failed',
  'updateStandardOption.invoke': 'Standard shipping clicked',
  'updateStandardOption.aborted': 'Standard shipping aborted (race guard)',
  'updateStandardOption.success': 'Standard shipping updated',
  'updateStandardOption.error': 'Standard shipping update failed',
  'turnstile.verify.call': 'Turnstile verification',
  'turnstile.verify.ok': 'Turnstile verified',
  'turnstile.verify.failed': 'Turnstile verify failed',
  'turnstile.verify.error': 'Turnstile verify error',
  'turnstile.verify.thrown': 'Turnstile verify threw',
  'turnstile.success': 'Turnstile passed',
  'turnstile.error': 'Turnstile error',
  'turnstile.expired': 'Turnstile expired',
  'turnstile.timeout': 'Turnstile timeout',
  'turnstile.rendered': 'Turnstile rendered',
  'turnstile.reset': 'Turnstile reset',
  'turnstile.unsupported': 'Turnstile unsupported',
  'turnstile.script.missing': 'Turnstile script missing',
  'turnstile.container.mounted': 'Turnstile container mounted',
  'turnstile.container.unmounted': 'Turnstile container unmounted',
  'paymentForm.mounted': 'Payment form mounted',
  'paymentForm.unmounted': 'Payment form unmounted',
  'handleSingleShippingSubmit.call': 'Shipping submit called',
  'handleSingleShippingSubmit.updateBillingAddress.skipped': 'Billing-same-as-shipping skipped',
  'handleSingleShippingSubmit.updateBillingAddress.pushed': 'Billing pushed to BC',
  'Checkout.handleShippingNextStep': 'Shipping → next step',
  'checkout.staleCheckoutId.recovery': 'Stale checkout id — recovered',
  'checkout.authRequired.recovery': 'Auth-required 403 — recovered',
};

// Place-order pipeline markers
const PO_ENTERED = 'Payment.handleSubmit.entered';
const ORDER_SUCCESS = 'order.submit.success';
const RECOVERY_EVENTS = new Set(['checkout.staleCheckoutId.recovery', 'checkout.authRequired.recovery']);
const ERROR_EVENTS = new Set(['Payment.handleSubmit.submitOrder.error', 'checkout.unhandledError']);

// Drop-off stage classifier — most-advanced wins.
function dropOffStage(events) {
  let stage = 'Initial';
  for (const e of events) {
    const ev = e.event || '';
    if (ev === 'paymentForm.mounted' ||
        (ev === 'turnstile.container.mounted' && e.data?.context === 'payment') ||
        ev === 'Payment.handleSubmit.branching' ||
        ev === 'order.preSubmit.snapshot') {
      return 'Payment'; // can't go higher
    }
    if (ev === 'handleSingleShippingSubmit.call' || ev === 'Checkout.handleShippingNextStep') {
      if (stage !== 'Payment') stage = 'ShippingSubmitted';
    } else if (
      ev.startsWith('Shipping.') ||
      ev.startsWith('calendar.') ||
      ev.startsWith('setShippingRate.') ||
      ev.startsWith('updateSelectedOption.') ||
      ev.startsWith('updateStandardOption.') ||
      ev.startsWith('GoogleAutocomplete.getDetails.')
    ) {
      if (stage === 'Initial') stage = 'Shipping';
    }
  }
  return stage;
}

const STAGE_META = {
  Payment: { label: '💳 Payment', color: 'red', rank: 3 },
  ShippingSubmitted: { label: '📮 Ship submitted', color: 'yellow', rank: 2 },
  Shipping: { label: '📦 Shipping', color: 'blue', rank: 1 },
  Initial: { label: '👤 Initial', color: 'muted', rank: 0 },
};

// Event level classification (the indicator pill).
function eventLevel(eventName) {
  const n = (eventName || '').toLowerCase();
  if (/error|failed|unhandled/.test(n)) return 'ERROR';
  if (/aborted|warn|recovery/.test(n))   return 'WARN';
  if (/success|completed/.test(n))       return 'OK';
  return 'INFO';
}

function friendlyName(eventName) {
  if (!eventName) return '(unknown)';
  if (FRIENDLY_NAMES[eventName]) return FRIENDLY_NAMES[eventName];
  // Fallback: last two segments, humanized.
  const parts = eventName.split('.');
  const tail = parts.length >= 2 ? parts.slice(-2) : parts;
  return tail.map(s => s.replace(/([a-z])([A-Z])/g, '$1 $2'))
             .map(s => s.charAt(0).toUpperCase() + s.slice(1))
             .join(' — ');
}

// Summary keys priority list — first hits win, take up to 3.
const SUMMARY_KEYS = ['errorMessage', 'message', 'errorCode', 'error', 'orderId', 'cartId', 'shippingMethod', 'shippingMethodId', 'shippingDivergence', 'url', 'route', 'name'];

function summarize(data) {
  if (!data || typeof data !== 'object') return '';
  const parts = [];
  for (const k of SUMMARY_KEYS) {
    if (data[k] !== undefined && data[k] !== null && data[k] !== '') {
      let v = data[k];
      if (typeof v === 'object') v = JSON.stringify(v);
      else v = String(v);
      if (v.length > 80) v = v.slice(0, 77) + '…';
      parts.push(`${k}=${v}`);
      if (parts.length === 3) return parts.join(' · ');
    }
  }
  if (parts.length > 0) return parts.join(' · ');
  // Fallback: first 3 scalar fields
  const scalar = [];
  for (const [k, v] of Object.entries(data)) {
    if (v == null) continue;
    if (typeof v === 'object') continue;
    let s = String(v);
    if (s.length > 80) s = s.slice(0, 77) + '…';
    scalar.push(`${k}=${s}`);
    if (scalar.length === 3) break;
  }
  return scalar.join(' · ');
}

// ───────────────────────────────────────────────────────────────────────────
// Aggregate computation — friction-focused. The core invariant:
// attempted = completed + inFlight + errored + stuck + recovered.
// COMPLETED dedupes by orderId (NOT sessionId) — telemetry retries can fire
// order.submit.success more than once for the same order.
// ───────────────────────────────────────────────────────────────────────────
const STUCK_THRESHOLD_MS = 90 * 1000;

function computeAggregates(events, nowMs) {
  const sessions = new Map(); // sessionId -> { events: [], lastTs, hasPO, hasSuccess, orderId, hasError, hasRecovery, browser, ip, name }
  const orderIds = new Set();
  let visitingSessions = 0;

  // Identity-bearing field paths in event.data, in priority order. The first
  // match per session wins. Names are nested under different keys depending
  // on which event fires (billing.name on preSubmit.snapshot, currentShipping.name
  // on shipping submit, shippingNow.name on billing-address writes, etc.).
  // The list endpoint strips clientIp/UA/referer but does NOT strip data.* —
  // these all survive the PII projection.
  const NAME_PATHS  = ['shipping', 'currentShipping', 'updatedShipping', 'shippingNow', 'billing', 'currentBilling', 'writingBilling', 'shippingSnapshot', 'billingSnapshot'];
  const PLACE_PATHS = NAME_PATHS;
  const EMAIL_PATHS = NAME_PATHS;
  function extractIdentity(data, target) {
    if (!data || typeof data !== 'object') return;
    if (!target.customer) {
      for (const p of NAME_PATHS) {
        const n = data[p]?.name;
        if (n && typeof n === 'string') {
          // BC sometimes stores names ALL-CAPS — normalize to title case.
          target.customer = n.replace(/\b\w[\w']*/g, w => w[0] + w.slice(1).toLowerCase());
          break;
        }
      }
    }
    if (!target.place) {
      for (const p of PLACE_PATHS) {
        const obj = data[p];
        if (obj && (obj.city || obj.state || obj.zip)) {
          const parts = [obj.city, obj.state].filter(Boolean);
          target.place = parts.join(', ') + (obj.zip ? ' ' + obj.zip : '');
          break;
        }
      }
    }
    if (!target.email) {
      for (const p of EMAIL_PATHS) {
        const em = data[p]?.email;
        if (em && typeof em === 'string' && em.includes('@')) {
          target.email = em.toLowerCase();
          break;
        }
      }
      if (!target.email) {
        const em = data.email || data.customerEmail;
        if (em && typeof em === 'string' && em.includes('@')) target.email = em.toLowerCase();
      }
    }
  }

  for (const e of events) {
    const sid = e.sessionId;
    if (!sid) continue;
    let s = sessions.get(sid);
    if (!s) {
      s = { sessionId: sid, events: [], lastTs: 0, hasPO: false, poCount: 0, hasSuccess: false, orderId: null, hasError: false, errorMessage: null, hasRecovery: false, browser: '', ip: '', customer: '', place: '', email: '', cartId: null };
      sessions.set(sid, s);
    }
    s.events.push(e);
    if (e.timestamp && e.timestamp > s.lastTs) s.lastTs = e.timestamp;
    if (e.event === PO_ENTERED) { s.hasPO = true; s.poCount++; }
    if (e.event === ORDER_SUCCESS) {
      s.hasSuccess = true;
      const oid = e.data?.orderId;
      if (oid != null) { s.orderId = oid; orderIds.add(String(oid)); }
    }
    if (RECOVERY_EVENTS.has(e.event)) s.hasRecovery = true;
    if (ERROR_EVENTS.has(e.event)) {
      s.hasError = true;
      s.errorMessage = s.errorMessage || e.data?.errorMessage || e.data?.message || e.data?.error || e.event;
    }
    // Best-effort context fields
    if (e.userAgent && !s.browser) s.browser = e.userAgent;
    if (e.clientIp && !s.ip) s.ip = e.clientIp;
    if (e.data?.cartId && !s.cartId) s.cartId = e.data.cartId;
    // Customer name + city/state come from the same nested objects.
    // extractIdentity walks NAME_PATHS / PLACE_PATHS and normalizes
    // ALL-CAPS names to title case (BC stores both shapes).
    extractIdentity(e.data, s);
  }
  visitingSessions = sessions.size;

  // Classify each PO session
  let attempted = 0, errored = 0, stuck = 0, recovered = 0, inFlight = 0;
  const poSessions = [];
  const dropOffSessions = [];
  for (const s of sessions.values()) {
    if (!s.hasPO) {
      // Drop-off candidate
      const stage = dropOffStage(s.events);
      dropOffSessions.push({ ...s, stage, stageMeta: STAGE_META[stage] });
      continue;
    }
    attempted++;
    let status;
    if (s.hasSuccess) status = 'success';
    else if (s.hasRecovery) { status = 'recovered'; recovered++; }
    else if (s.hasError) { status = 'error'; errored++; }
    else {
      const idleMs = nowMs - s.lastTs;
      if (idleMs > STUCK_THRESHOLD_MS) { status = 'stuck'; stuck++; }
      else { status = 'in-flight'; inFlight++; }
    }
    poSessions.push({ ...s, status });
  }
  const completed = orderIds.size;

  // Conversion: completed / max(0, attempted - recovered). Suppress when n<5.
  const effectiveAttempts = Math.max(0, attempted - recovered);
  let conversion = null;
  if (effectiveAttempts >= 5) conversion = completed / effectiveAttempts;

  // Concurrency Guard: aborted/invoke ratio across both invoke/aborted families
  let invokes = 0, aborts = 0;
  for (const e of events) {
    if (e.event === 'updateSelectedOption.invoke' || e.event === 'updateStandardOption.invoke') invokes++;
    if (e.event === 'updateSelectedOption.aborted' || e.event === 'updateStandardOption.aborted') aborts++;
  }

  // Deploy Health: count of preSubmit snapshots with shippingDivergence non-null
  let divergent = 0;
  for (const e of events) {
    if (e.event === 'order.preSubmit.snapshot' && e.data?.shippingDivergence != null) divergent++;
  }

  return {
    visitingSessions,
    attempted,
    completed,
    errored,
    stuck,
    recovered,
    inFlight,
    conversion,
    effectiveAttempts,
    poSessions,
    dropOffSessions,
    sessions,
    orderIds,
    invokes,
    aborts,
    divergent,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Summary categorization — finer-grained than the dashboard tiles. Splits
// errors into card_declined / avs_error / turnstile_fail / submit_other,
// and drop-offs into walked_away / payment_drop / shipping_drop / initial_drop.
// Used by the Summary tab.
// ───────────────────────────────────────────────────────────────────────────

function classifySessionFine(s) {
  if (s.hasSuccess) return 'success';
  if (s.hasRecovery) return 'recovered';
  if (s.hasPO && s.hasError) {
    const msg = (s.errorMessage || '').toLowerCase();
    if (/declined|denied|insufficient|lost|stolen|expired card|do not honor|generic decline/.test(msg)) return 'card_declined';
    if (/billing address couldn'?t be verified|avs|address verification|address mismatch|address not on file/.test(msg)) return 'avs_error';
    if (/turnstile|cloudflare challenge|challenge.*token/.test(msg)) return 'turnstile_fail';
    return 'submit_other';
  }
  if (s.hasPO) {
    // PO clicked, no terminal state — stuck or in-flight (we don't separate
    // these in the period summary; dashboard already shows in-flight live)
    return 'stuck';
  }
  // No PO clicked — drop-off. Use stage as a proxy.
  // walked_away: at least one page.blur AND made it to payment-stage events.
  let blurCount = 0;
  let reachedPayment = false;
  let reachedShipping = false;
  for (const e of s.events) {
    if (e.event === 'page.blur') blurCount++;
    if (e.event === 'paymentForm.mounted' || e.event === 'Payment.handleSubmit.branching' ||
        (e.event === 'turnstile.container.mounted' && e.data?.context === 'payment')) reachedPayment = true;
    if (e.event === 'handleSingleShippingSubmit.call' || e.event === 'Checkout.handleShippingNextStep') reachedShipping = true;
  }
  if (reachedPayment && blurCount >= 1) return 'walked_away';
  if (reachedPayment) return 'payment_drop';
  if (reachedShipping) return 'shipping_drop';
  return 'initial_drop';
}

// Industry-average bands for each category, expressed as a fraction of
// "attempted" (sessions with PO_ENTERED) for failure modes, or "all sessions"
// for drop-offs. Conservative bands sourced from public BC + Baymard data;
// tune as we learn what's normal for crabplace.com specifically.
//
// good: ≤ this fraction is healthy
// warn: > good is yellow, > warn is red
const SUMMARY_BENCHMARKS = {
  // Failure modes — denominator: attempted (sessions that clicked Place Order)
  card_declined:  { label: 'Card declined',          basis: 'attempted', good: 0.05, warn: 0.10, note: 'Industry average 5–10% (Baymard, BC). Higher: card files aging, fraud filter overactive, or VIP renewal cards expiring.' },
  avs_error:      { label: 'AVS / address verify',   basis: 'attempted', good: 0.03, warn: 0.07, note: 'Industry average 3–5%. Higher: AVS strictness setting too tight, or many shipping ≠ billing customers.' },
  turnstile_fail: { label: 'Turnstile failed',       basis: 'attempted', good: 0.01, warn: 0.03, note: 'Cloudflare bot-challenge fails. Higher: token-aging or unmount-before-complete pattern.' },
  submit_other:   { label: 'Other submit error',     basis: 'attempted', good: 0.02, warn: 0.05, note: 'Submit errors not classified above. Worth investigating each session.' },
  stuck:          { label: 'Stuck (no terminal)',    basis: 'attempted', good: 0.02, warn: 0.05, note: 'Clicked Place Order, no success/error/recovery in window. Often customer closed the tab mid-submit.' },
  // Drop-offs — denominator: visiting (all sessions)
  walked_away:    { label: 'Walked away',            basis: 'visiting',  good: 0.30, warn: 0.50, note: 'Reached payment, tabbed away, never came back. Normal up to ~30%; >50% suggests a UX or trust issue at payment.' },
  payment_drop:   { label: 'Payment-stage drop',     basis: 'visiting',  good: 0.05, warn: 0.10, note: 'Reached payment but no blur and no PO click. May indicate form confusion or pricing surprise at the last step.' },
  shipping_drop:  { label: 'Shipping-stage drop',    basis: 'visiting',  good: 0.10, warn: 0.20, note: 'Submitted shipping but bailed before payment. Often shipping cost / delivery date friction.' },
  initial_drop:   { label: 'Initial-stage drop',     basis: 'visiting',  good: 0.40, warn: 0.60, note: 'Landed on /checkout and never engaged. Industry average 40–60% for this earliest funnel slice.' },
  // Recovery — informational; ratio of recoveries to attempted
  recovered:      { label: 'Recoveries',             basis: 'attempted', good: 0.02, warn: 0.05, note: 'Stale-checkoutId or auth-required bounces through /cart. >5% means our session-id handling needs work.' },
  // Concurrency Guard — informational
  abort_rate:     { label: 'Race-guard aborts',      basis: 'invokes',   good: 0.50, warn: 0.80, note: 'Aborts/invokes ratio for shipping clicks. >50% is high-frequency double-clicking — guard is doing its job.' },
  // Deploy Health
  shipping_div:   { label: 'shippingDivergence',     basis: 'attempted', good: 0.001, warn: 0.005, note: 'Real bug: BC consignment != address customFields. Should be near zero. Any non-zero is investigation-worthy.' },
};

// Compute the summary roll-up from the existing aggregates + sessions.
function computeSummary(agg) {
  const buckets = { success: 0, recovered: 0, card_declined: 0, avs_error: 0, turnstile_fail: 0, submit_other: 0, stuck: 0, walked_away: 0, payment_drop: 0, shipping_drop: 0, initial_drop: 0 };
  for (const s of agg.sessions.values()) {
    const cat = classifySessionFine(s);
    if (cat in buckets) buckets[cat]++;
  }
  // Ratio against the right denominator. Ratios are null when denominator is 0.
  function ratio(num, basis) {
    let den;
    if (basis === 'attempted') den = agg.attempted;
    else if (basis === 'visiting') den = agg.visitingSessions;
    else if (basis === 'invokes') den = agg.invokes;
    return den > 0 ? num / den : null;
  }
  const rows = [];
  for (const [key, b] of Object.entries(SUMMARY_BENCHMARKS)) {
    let count;
    if (key === 'abort_rate') { count = agg.aborts; }
    else if (key === 'shipping_div') { count = agg.divergent; }
    else { count = buckets[key] || 0; }
    const r = ratio(count, b.basis);
    let status = 'ok';
    if (r != null) {
      if (r > b.warn) status = 'red';
      else if (r > b.good) status = 'yellow';
    }
    rows.push({ key, label: b.label, basis: b.basis, count, ratio: r, good: b.good, warn: b.warn, status, note: b.note });
  }
  return { buckets, rows };
}

// ───────────────────────────────────────────────────────────────────────────
// Tile color thresholds
// ───────────────────────────────────────────────────────────────────────────
function colorErrored(n)   { return n === 0 ? 'green' : n <= 2 ? 'yellow' : 'red'; }
function colorStuck(n)     { return n === 0 ? 'green' : n <= 2 ? 'yellow' : 'red'; }
function colorRecovered(total, rate) {
  if (total === 0) return 'green';
  if (total >= 3 && rate >= 0.05) return 'red';
  return 'yellow';
}
function colorConversion(c) {
  if (c == null) return 'muted';
  if (c >= 0.85) return 'green';
  if (c >= 0.70) return 'yellow';
  return 'red';
}
function colorAbortRate(rate) { return rate < 0.5 ? 'green' : 'yellow'; }
function colorDivergent(n)    { return n === 0 ? 'green' : 'red'; }

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────
function isoDate(d) { return d.toISOString().slice(0, 10); }
function fmtTs(ms) {
  if (!ms) return '';
  const d = new Date(typeof ms === 'number' ? ms : ms);
  if (isNaN(d.getTime())) return String(ms);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
function fmtAgo(ms, nowMs) {
  if (!ms) return '';
  const sec = Math.max(0, Math.floor((nowMs - ms) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m ago`;
}
function fmtDuration(ms) {
  if (ms == null || ms < 0) return '';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}
// Pill + inline R2 audio player for a matched CS call. Shown on stuck/drop-off
// rows when /checkout-call-cross-ref found a call from this customer within
// the time window after their last checkout event. Click ▶ to load + play
// the recording (streamed from R2 via the archive-recordings function).
function CsCallBadge({ session, callMatches }) {
  const call = callMatches?.get(session.sessionId);
  const [state, setState] = useState('idle'); // idle | loading | ready | playing | error
  const audioRef = useRef(null);
  const blobRef = useRef(null);

  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch {} audioRef.current = null; }
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
  }, []);

  if (!call) return null;
  const delta = new Date(call.contact_date).getTime() - session.lastTs;

  const togglePlay = async (e) => {
    e.stopPropagation(); // don't trigger the row's onSelect
    if (state === 'loading') return;
    if (audioRef.current) {
      if (state === 'playing') { audioRef.current.pause(); setState('ready'); }
      else { audioRef.current.play(); setState('playing'); }
      return;
    }
    setState('loading');
    try {
      const res = await fetch('/.netlify/functions/archive-recordings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'stream', params: { r2Key: call.r2_key } }),
      });
      if (!res.ok) throw new Error('stream failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.addEventListener('ended', () => setState('ready'));
      audio.addEventListener('error', () => setState('error'));
      await audio.play();
      setState('playing');
    } catch { setState('error'); }
  };

  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4,
      padding: '2px 7px 2px 5px', borderRadius: 3,
      background: '#fff4e0', color: '#8a4a0d', fontSize: 10, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase',
    }}>
      <button
        onClick={togglePlay}
        disabled={state === 'loading' || state === 'error'}
        title={state === 'error' ? 'Failed to load recording' : 'Play recording'}
        style={{
          background: state === 'playing' ? '#8a4a0d' : 'transparent',
          border: '1px solid #8a4a0d', borderRadius: '50%', width: 16, height: 16,
          padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          cursor: state === 'loading' ? 'wait' : 'pointer', flexShrink: 0,
        }}>
        {state === 'loading' ? <RefreshCw size={9} color="#8a4a0d" style={{ animation: 'spin 1s linear infinite' }} />
          : state === 'playing' ? <Pause size={9} color="#fff" />
          : <Play size={9} color="#8a4a0d" style={{ marginLeft: 1 }} />}
      </button>
      <Phone size={10} />
      <span>CS call</span>
      {call.is_outbound ? <span style={{ opacity: 0.7, fontWeight: 500 }}>(outbound)</span> : null}
      {delta > 0 && <span style={{ marginLeft: 2, fontWeight: 500, opacity: 0.8 }}>+{fmtDuration(delta)}</span>}
      {call.agent_name && <span style={{ marginLeft: 2, fontWeight: 500, opacity: 0.7 }}>· {call.agent_name}</span>}
    </div>
  );
}

// Pill rendered on stuck/drop-off rows when a later success event was found
// for the same sessionId OR cartId in the 7-day completions maps. Distinguishes
// a true unresolved checkout from one whose resolution simply landed outside
// the active window — including the common case where the customer reloaded
// the page (new sessionId) but kept the same cartId.
function CompletedLaterBadge({ session, sidMap, cartMap }) {
  let completion = sidMap?.get(session.sessionId);
  let viaCart = false;
  if (!completion && session.cartId) {
    const c = cartMap?.get(session.cartId);
    if (c) { completion = c; viaCart = true; }
  }
  if (!completion) return null;
  const delta = completion.ts - session.lastTs;
  return (
    <span style={{
      display: 'inline-block', marginTop: 4, padding: '2px 7px', borderRadius: 3,
      background: '#e6f4e6', color: '#1f6b32', fontSize: 10, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase',
    }}>
      ✓ Completed later
      {completion.orderId != null && <span style={{ fontFamily: '"SF Mono", Menlo, monospace', marginLeft: 6, fontWeight: 600 }}>#{completion.orderId}</span>}
      {delta > 0 && <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.8 }}>+{fmtDuration(delta)}</span>}
      {viaCart && <span style={{ marginLeft: 6, fontWeight: 500, opacity: 0.7 }}>(diff session)</span>}
    </span>
  );
}
function browserFromUA(ua) {
  if (!ua) return '';
  if (/iPhone|iPad/i.test(ua)) return 'iOS';
  if (/Android/i.test(ua)) return 'Android';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Other';
}

async function fetchTelemetry(params) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== '' && v !== null && v !== undefined) qs.set(k, v);
  }
  const t0 = Date.now();
  const res = await fetch('/.netlify/functions/checkout-telemetry?' + qs.toString());
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch (_) { data = { error: 'non-json', raw: text.slice(0, 500) }; }
  return { httpStatus: res.status, data, durationMs: Date.now() - t0 };
}

// ───────────────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────────────
function LevelPill({ level }) {
  const c = LEVEL_BADGE[level] || LEVEL_BADGE.INFO;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', background: c.bg, color: c.fg, minWidth: 50, textAlign: 'center' }}>
      {level || 'INFO'}
    </span>
  );
}

function StatusBadge({ status }) {
  const meta = {
    'success':   { label: '✅ success',   color: 'green' },
    'in-flight': { label: '⏳ in-flight', color: 'blue' },
    'recovered': { label: '↻ recovered',  color: 'yellow' },
    'error':     { label: '❌ error',      color: 'red' },
    'stuck':     { label: '⚠️ stuck',     color: 'yellow' },
  }[status] || { label: status, color: 'muted' };
  const c = TILE_COLORS[meta.color];
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10.5, fontWeight: 700, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {meta.label}
    </span>
  );
}

function StageBadge({ stage }) {
  const meta = STAGE_META[stage] || STAGE_META.Initial;
  const c = TILE_COLORS[meta.color];
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10.5, fontWeight: 700, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {meta.label}
    </span>
  );
}

function EnvToggle({ env, onChange }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
      {['prod', 'sandbox'].map(e => (
        <button key={e} onClick={() => onChange(e)}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none',
            background: env === e ? (e === 'prod' ? 'var(--orange)' : 'var(--navy)') : '#fff',
            color: env === e ? '#fff' : 'var(--gray-700)', cursor: 'pointer',
          }}>
          {e === 'prod' ? 'Production' : 'Sandbox-1'}
        </button>
      ))}
    </div>
  );
}

function Tile({ label, value, sub, color, onClick }) {
  const c = TILE_COLORS[color] || TILE_COLORS.muted;
  return (
    <div onClick={onClick} style={{
      ...CARD,
      borderTop: `3px solid ${c.border}`,
      padding: '10px 14px',
      cursor: onClick ? 'pointer' : 'default',
      minHeight: 76,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: c.fg, lineHeight: 1.05, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function StatBlock({ label, value, sub, color }) {
  const c = TILE_COLORS[color] || TILE_COLORS.muted;
  return (
    <div style={{ flex: 1, padding: '8px 12px', borderLeft: `3px solid ${c.border}` }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: c.fg, marginTop: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function EventRow({ event, expanded, onToggle, onSelect, selected }) {
  const lvl = eventLevel(event.event);
  const summary = summarize(event.data);
  const friendlyN = friendlyName(event.event);
  const hasData = event.data && typeof event.data === 'object' && Object.keys(event.data).length > 0;
  return (
    <div style={{
      padding: '6px 10px',
      borderBottom: '1px solid var(--gray-100)',
      background: selected ? 'var(--gray-50)' : 'transparent',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11.5 }}>
        <div style={{ width: 60, flexShrink: 0 }}><LevelPill level={lvl} /></div>
        <div style={{ width: 220, flexShrink: 0, fontWeight: 600, color: 'var(--gray-800)' }}>{friendlyN}</div>
        <div style={{ flex: 1, fontFamily: '"SF Mono", Menlo, Consolas, monospace', fontSize: 11, color: 'var(--gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</div>
        <div style={{ width: 78, flexShrink: 0, fontFamily: '"SF Mono", Menlo, monospace', fontSize: 10.5, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtTs(event.timestamp).split(',').pop()?.trim()}</div>
        <div style={{ width: 200, flexShrink: 0, fontFamily: '"SF Mono", Menlo, monospace', fontSize: 10.5, color: 'var(--gray-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.event}</div>
        {hasData ? (
          <button onClick={onToggle} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--gray-600)' }}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : <div style={{ width: 18 }} />}
      </div>
      {expanded && hasData && (
        <div style={{ ...CODE, marginTop: 4 }}>{JSON.stringify(event.data, null, 2)}</div>
      )}
    </div>
  );
}

function AiSummaryCard({ summary, loading, error, eventCount }) {
  if (loading) {
    return (
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, color: 'var(--gray-600)', fontSize: 12.5, background: '#f5f8fc' }}>
        <div className="spin" style={{ width: 12, height: 12, border: '2px solid var(--gray-300)', borderTopColor: 'var(--navy)', borderRadius: '50%' }} />
        Claude is reviewing the {eventCount} events…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '14px 16px', background: '#fde6e6', color: '#7a1818', fontSize: 12 }}>
        Summary failed ({error.status}): <code>{JSON.stringify(error.body).slice(0, 200)}</code>
      </div>
    );
  }
  if (!summary) return null;

  const sevColor = TILE_COLORS[summary.severity === 'error' ? 'red' : summary.severity === 'warn' ? 'yellow' : 'blue'];

  return (
    <div style={{ background: '#f5f8fc', borderBottom: '1px solid var(--gray-200)', padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          fontSize: 9.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase',
          background: sevColor.bg, color: sevColor.fg, padding: '3px 7px', borderRadius: 3,
        }}>
          {summary.severity || 'info'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          AI summary
        </span>
        {summary.knownBugClass && summary.knownBugClass !== 'other' && (
          <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'var(--gray-100)', color: 'var(--gray-700)', fontFamily: '"SF Mono", Menlo, monospace' }}>
            {summary.knownBugClass}
          </span>
        )}
        <span style={{ fontSize: 10.5, color: 'var(--gray-500)', marginLeft: 'auto' }}>
          {summary.modelUsed} · {summary.sampledCount}/{summary.eventCount} events analyzed · {summary.durationMs ? `${(summary.durationMs / 1000).toFixed(1)}s` : ''}
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--gray-800)', lineHeight: 1.5, marginBottom: 8 }}>
        {summary.summary}
      </div>
      {summary.rootCause && (
        <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.5, marginBottom: 8 }}>
          <strong style={{ color: 'var(--gray-500)', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4 }}>Root cause: </strong>
          {summary.rootCause}
        </div>
      )}
      {summary.suggestedFix && (
        <div style={{ fontSize: 12, color: 'var(--gray-800)', lineHeight: 1.5, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 4, padding: '8px 10px' }}>
          <strong style={{ color: 'var(--navy)', textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.4, display: 'block', marginBottom: 4 }}>Suggested next step</strong>
          {Array.isArray(summary.suggestedFix) ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {summary.suggestedFix.map((item, i) => <li key={i} style={{ marginBottom: 3 }}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>)}
            </ul>
          ) : (
            <span>{summary.suggestedFix}</span>
          )}
        </div>
      )}
    </div>
  );
}

function StatusLine({ status }) {
  const dotColor = status.state === 'polling' ? '#58a6ff' :
                   status.state === 'error'   ? '#f85149' :
                                                '#3fb950';
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--gray-600)' }}>
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dotColor, animation: status.state === 'polling' ? 'pulse 1.2s ease-in-out infinite' : 'none' }} />
      <span>{status.msg}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const ROLLING_HOURS = 4;
const FETCH_LIMIT = 1000;
const HISTORY_DAYS = 7;

// Range presets for the dashboard. A range resolves to (sinceMs, untilMs, polled).
// `polled` keeps the 5-min auto-refresh on; past-day windows pause it
// (immutable data; no point burning Cosmos round-trips).
const RANGES = [
  { id: 'live',      label: 'Live (4h)',  polled: true,  resolve: () => ({ sinceMs: Date.now() - ROLLING_HOURS * 3600 * 1000, untilMs: Date.now() }) },
  { id: '24h',       label: 'Last 24h',   polled: true,  resolve: () => ({ sinceMs: Date.now() - 24 * 3600 * 1000, untilMs: Date.now() }) },
  { id: 'today',     label: 'Today UTC',  polled: true,  resolve: () => { const s = Date.parse(new Date().toISOString().slice(0, 10) + 'T00:00:00Z'); return { sinceMs: s, untilMs: Math.min(s + 24 * 3600 * 1000, Date.now()) }; } },
  { id: 'yesterday', label: 'Yesterday',  polled: false, resolve: () => { const s = Date.parse(new Date().toISOString().slice(0, 10) + 'T00:00:00Z') - 24 * 3600 * 1000; return { sinceMs: s, untilMs: s + 24 * 3600 * 1000 }; } },
  { id: 'custom',    label: 'Custom',     polled: false, resolve: (date) => { const s = Date.parse(date + 'T00:00:00Z'); return { sinceMs: s, untilMs: s + 24 * 3600 * 1000 }; } },
];

export default function CheckoutTelemetry() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('IT') || userRoles.includes('Manager');

  // Top-level state
  const [env, setEnv] = useState('prod');
  const [surface, setSurface] = useState('checkout'); // 'user' | 'checkout'
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'summary' | 'raw'
  const [rangeId, setRangeId] = useState('live'); // see RANGES
  const [customDate, setCustomDate] = useState(''); // YYYY-MM-DD UTC, used when rangeId='custom'
  const range = RANGES.find(r => r.id === rangeId) || RANGES[0];
  const isPolled = range.polled && (rangeId !== 'custom' || !customDate);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState(null); // { day, attempted, completed }[]
  // Maps from the 7-day ORDER_SUCCESS fetch so a stuck/drop-off session whose
  // success event landed outside the active window can be flagged "completed
  // later" instead of looking unresolved. Two indexes:
  //   - bySid:    same-session match (the success event has the same sessionId)
  //   - byCartId: cross-session match (customer reloaded → new sessionId, but
  //               the BC cartId persists across reloads)
  const [completionsBySid, setCompletionsBySid] = useState(() => new Map());
  const [completionsByCartId, setCompletionsByCartId] = useState(() => new Map());
  // sessionId → call_recordings row (or null) — populated by /checkout-call-cross-ref
  // for stuck/drop-off sessions whose customer placed a CS call within ~4h
  // after their last checkout event.
  const [callMatches, setCallMatches] = useState(() => new Map());
  const [cartEmails, setCartEmails] = useState(() => new Map());
  const [status, setStatus] = useState({ state: 'idle', msg: 'Idle' });
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Selection (shared across all three lists in dashboard view)
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [selectedSessionEvents, setSelectedSessionEvents] = useState([]);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  // AI summary (Claude-generated triage card for the selected session)
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [eventsExpanded, setEventsExpanded] = useState(false); // collapse raw log by default

  // Raw-view state
  const [rawSince, setRawSince] = useState('');
  const [rawUntil, setRawUntil] = useState('');
  const [rawLevels, setRawLevels] = useState(new Set(['error', 'warn', 'info']));
  const [rawEventFilter, setRawEventFilter] = useState('');
  const [rawSessionId, setRawSessionId] = useState('');
  const [rawCartId, setRawCartId] = useState('');
  const [rawOrderId, setRawOrderId] = useState('');
  const [rawSelectedId, setRawSelectedId] = useState(null);

  const pollIntervalRef = useRef(null);

  // ────────────────────────────────────────────────────────────────────
  // Data fetching (dashboard mode)
  // ────────────────────────────────────────────────────────────────────
  const fetchDashboard = useCallback(async (kind = 'manual') => {
    setError(null);
    // Window bounds in epoch ms — driven by the active range preset.
    if (rangeId === 'custom' && !customDate) {
      // Custom range with no date picked → idle, don't fetch.
      setStatus({ state: 'idle', msg: 'Pick a date to load events.' });
      return;
    }
    const { sinceMs, untilMs } = range.resolve(customDate);

    // Incremental sync: pull only events newer than the last recorded
    // high-water mark from Cosmos and mirror them to Neon. Reads then come
    // from Neon (fast). Prod only — sandbox events bypass this. Best-effort:
    // a sync error doesn't block the read since Neon already has data.
    if (env === 'prod') {
      try {
        setStatus({ state: 'polling', msg: 'Syncing new events…' });
        await fetch('/.netlify/functions/checkout-telemetry?env=prod&action=sync');
      } catch { /* swallow — fall through to read */ }
    }

    // The webapp endpoint returns events newest-first, capped at FETCH_LIMIT
    // per request. To capture the entire window we walk backwards via a
    // time cursor on `until`: each page's oldest timestamp - 1 ms becomes
    // the next page's upper bound, until we either get a partial page
    // (full window covered) or hit MAX_PAGES (safety cap so we can't loop
    // forever on a misbehaving endpoint).
    //
    // 2026-05-08: prod day volumes are ~30-60k events on a busy day, so a
    // 24h window at FETCH_LIMIT=1000 needs 30-60 round trips. The webapp
    // rate-limits at 60/min global; 50 is the practical headroom. If the
    // webapp's per-call cap gets bumped (1000 → 5000), pages drop ~5×.
    const MAX_PAGES = 50;
    const t0 = Date.now();
    const seenIds = new Set();
    const all = [];
    let cursor = untilMs;
    let pages = 0;
    let truncated = false;

    while (pages < MAX_PAGES) {
      pages++;
      setStatus({
        state: 'polling',
        msg: `Loading ${range.label}${rangeId === 'custom' && customDate ? ' ' + customDate : ''}… (page ${pages})`,
      });
      const { httpStatus, data } = await fetchTelemetry({
        env, limit: FETCH_LIMIT,
        since: sinceMs,
        until: cursor,
      });
      if (httpStatus !== 200) {
        setError({ status: httpStatus, body: data });
        setStatus({ state: 'error', msg: `Poll failed on page ${pages} in ${((Date.now() - t0) / 1000).toFixed(1)}s` });
        return;
      }
      const events = Array.isArray(data) ? data : (data.events || []);
      let added = 0;
      let oldest = Infinity;
      for (const e of events) {
        const id = e._id || (e.timestamp + ':' + e.event + ':' + e.sessionId);
        if (seenIds.has(id)) continue;
        seenIds.add(id);
        all.push(e);
        added++;
        if (e.timestamp != null && e.timestamp < oldest) oldest = e.timestamp;
      }
      // Stop conditions: short page = covered the window; nothing new = at boundary;
      // oldest event already at-or-below since = covered.
      if (events.length < FETCH_LIMIT) break;
      if (added === 0) break;
      if (oldest === Infinity) break;
      if (oldest <= sinceMs) break;
      cursor = oldest - 1;
      if (pages === MAX_PAGES) truncated = true;
    }

    const durationMs = Date.now() - t0;

    // Filter to the active window (covers the rounding/safety overshoot)
    const list = all.filter(e => {
      const ts = e.timestamp || 0;
      return ts >= sinceMs && ts <= untilMs;
    });
    setEvents(list);
    setNow(Date.now());
    const sessionCount = new Set(list.map(e => e.sessionId).filter(Boolean)).size;
    const poCount = list.filter(e => e.event === PO_ENTERED).length;
    const scope = range.label + (rangeId === 'custom' && customDate ? ` · ${customDate}` : '');
    setStatus({
      state: truncated ? 'error' : 'idle',
      msg: `Loaded ${list.length} events · ${sessionCount} sessions · ${poCount} place-order · ${scope} · ${pages} page${pages > 1 ? 's' : ''} · ${(durationMs / 1000).toFixed(1)}s${truncated ? ` (TRUNCATED at ${MAX_PAGES} pages — increase MAX_PAGES or shorten window)` : ''}`,
    });
  }, [env, rangeId, customDate, range]); // eslint-disable-line

  // History per-day funnel — fetch the place-order + success markers for last 7 days
  const fetchHistory = useCallback(async () => {
    const params = { env, limit: FETCH_LIMIT, since: `P${HISTORY_DAYS}D` };
    const [poResp, okResp] = await Promise.all([
      fetchTelemetry({ ...params, event: PO_ENTERED }),
      fetchTelemetry({ ...params, event: ORDER_SUCCESS }),
    ]);
    if (poResp.httpStatus !== 200 || okResp.httpStatus !== 200) return;
    const poEvents = poResp.data.events || [];
    const okEvents = okResp.data.events || [];
    // Group by UTC date
    const byDay = new Map();
    function bucketFor(d) {
      const day = new Date(d).toISOString().slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, { day, attemptSessions: new Set(), orderIds: new Set() });
      return byDay.get(day);
    }
    for (const e of poEvents) {
      const b = bucketFor(e.timestamp);
      if (e.sessionId) b.attemptSessions.add(e.sessionId);
    }
    const sidMap = new Map();
    const cartMap = new Map();
    for (const e of okEvents) {
      const b = bucketFor(e.timestamp);
      if (e.data?.orderId != null) b.orderIds.add(String(e.data.orderId));
      else if (e.sessionId) b.orderIds.add('s:' + e.sessionId); // fallback
      // Index by sessionId AND cartId. Keep the earliest success per key —
      // an order that retries .success still resolved at the first one.
      const info = { orderId: e.data?.orderId ?? null, ts: e.timestamp };
      if (e.sessionId && e.timestamp) {
        const prev = sidMap.get(e.sessionId);
        if (!prev || e.timestamp < prev.ts) sidMap.set(e.sessionId, info);
      }
      if (e.data?.cartId && e.timestamp) {
        const prev = cartMap.get(e.data.cartId);
        if (!prev || e.timestamp < prev.ts) cartMap.set(e.data.cartId, info);
      }
    }
    setCompletionsBySid(sidMap);
    setCompletionsByCartId(cartMap);
    const rows = [...byDay.values()]
      .map(b => ({ day: b.day, attempted: b.attemptSessions.size, completed: b.orderIds.size }))
      .sort((a, b) => b.day.localeCompare(a.day));
    setHistory(rows);
  }, [env]);

  // Drill into a session — uses /sessions/:id which returns PII-bearing events.
  // After events load, fire the AI summary call in parallel with display so
  // the user sees the event timeline immediately and the summary card lands
  // a few seconds later.
  const drillSession = useCallback(async (sid) => {
    if (!sid) return;
    setSelectedSessionId(sid);
    setSelectedLoading(true);
    setSelectedSessionEvents([]);
    setExpandedRows({});
    setAiSummary(null);
    setAiError(null);
    setAiLoading(false);
    setEventsExpanded(false);
    const { httpStatus, data } = await fetchTelemetry({ env, sub: 'sessions/' + sid });
    let list = [];
    if (httpStatus === 200) {
      list = (data.events || []).slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
      setSelectedSessionEvents(list);
    }
    setSelectedLoading(false);
    if (list.length === 0) return;
    // Fire AI summary
    setAiLoading(true);
    try {
      const res = await fetch('/.netlify/functions/checkout-telemetry-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid, events: list }),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch (_) { parsed = { error: 'non-json', raw: text.slice(0, 500) }; }
      if (res.status !== 200) {
        setAiError({ status: res.status, body: parsed });
      } else {
        setAiSummary(parsed);
      }
    } catch (e) {
      setAiError({ status: 0, body: { error: e.message } });
    } finally {
      setAiLoading(false);
    }
  }, [env]);

  // Initial + on-change loads
  useEffect(() => {
    if (!isAllowed) return;
    setSelectedSessionId(null);
    setSelectedSessionEvents([]);
    fetchDashboard();
    fetchHistory();
  }, [env, rangeId, customDate, isAllowed]); // eslint-disable-line

  // Polling — paused on past-day windows (immutable data) and custom-without-date
  useEffect(() => {
    if (!isAllowed) return;
    if (!isPolled) return;
    if (view !== 'dashboard') return; // raw mode does its own fetching
    pollIntervalRef.current = setInterval(() => {
      fetchDashboard('poll');
      // Also update "now" so "ago" timers tick
      setNow(Date.now());
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollIntervalRef.current);
  }, [isPolled, env, view, isAllowed, fetchDashboard]);

  // "Now" ticker for relative timestamps in lists (every 15s; cheap)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  // Raw-view fetch
  const rawLoad = useCallback(async () => {
    setStatus({ state: 'polling', msg: 'Loading raw events…' });
    setError(null);
    const params = {
      env,
      since: rawSince || undefined,
      until: rawUntil || undefined,
      level: rawLevels.size === 3 ? '' : [...rawLevels].join(','),
      event: rawEventFilter || undefined,
      sessionId: rawSessionId || undefined,
      cartId: rawCartId || undefined,
      orderId: rawOrderId || undefined,
      limit: FETCH_LIMIT,
    };
    const { httpStatus, data, durationMs } = await fetchTelemetry(params);
    if (httpStatus !== 200) {
      setError({ status: httpStatus, body: data });
      setStatus({ state: 'error', msg: `Failed in ${(durationMs / 1000).toFixed(1)}s` });
      return;
    }
    setEvents(Array.isArray(data) ? data : (data.events || []));
    setStatus({ state: 'idle', msg: `Loaded ${(data.events || []).length} events · ${(durationMs / 1000).toFixed(1)}s` });
  }, [env, rawSince, rawUntil, rawLevels, rawEventFilter, rawSessionId, rawCartId, rawOrderId]);

  useEffect(() => {
    if (view === 'raw' && isAllowed) rawLoad();
  }, [view]); // eslint-disable-line

  // ────────────────────────────────────────────────────────────────────
  // Aggregates from current events
  // ────────────────────────────────────────────────────────────────────
  const agg = useMemo(() => computeAggregates(events, now), [events, now]);
  const userAgg = useMemo(() => computeUserAggregates(events, now), [events, now]);
  const recoveryRate = agg.attempted > 0 ? agg.recovered / agg.attempted : 0;
  const stuckList = useMemo(() =>
    agg.poSessions
      .filter(s => s.status === 'error' || s.status === 'stuck')
      .sort((a, b) => b.lastTs - a.lastTs)
  , [agg.poSessions]);
  const recentList = useMemo(() =>
    agg.poSessions.slice().sort((a, b) => b.lastTs - a.lastTs)
  , [agg.poSessions]);
  const dropOffList = useMemo(() => {
    const byRank = (a, b) => (b.stageMeta.rank - a.stageMeta.rank) || (b.lastTs - a.lastTs);
    return agg.dropOffSessions.slice().sort(byRank).slice(0, 50);
  }, [agg.dropOffSessions]);

  // Fetch CS-call cross-references for stuck/drop-off rows whenever the lists
  // change. Same-name customers + 4h time window is the join. Skip rows that
  // have no extracted name; nothing to match against.
  useEffect(() => {
    const candidates = [];
    for (const s of stuckList) {
      if (s.customer) candidates.push({ key: s.sessionId, name: s.customer, lastTs: s.lastTs });
    }
    for (const s of dropOffList) {
      if (s.customer) candidates.push({ key: s.sessionId, name: s.customer, lastTs: s.lastTs });
    }
    if (candidates.length === 0) { setCallMatches(new Map()); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/checkout-call-cross-ref', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ candidates }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const map = new Map();
        for (const [k, v] of Object.entries(data.matches || {})) if (v) map.set(k, v);
        setCallMatches(map);
      } catch { /* swallow — badge just won't appear */ }
    })();
    return () => { cancelled = true; };
  }, [stuckList, dropOffList]);

  // Fetch BC cart emails for rows that have a cartId but no email from telemetry
  // (which is most of them — the webapp strips the local-part at source). One
  // batched call per refresh; results merge into a Map keyed by cartId. Skip
  // rows that already have s.email or whose session reached a successful order
  // (BC deletes the cart on order completion).
  useEffect(() => {
    const ids = new Set();
    const collect = (list) => {
      for (const s of list) {
        if (!s.cartId) continue;
        if (s.email) continue;
        if (s.hasSuccess) continue; // cart likely gone
        if (cartEmails.has(s.cartId)) continue;
        ids.add(s.cartId);
      }
    };
    collect(stuckList);
    collect(recentList);
    collect(dropOffList);
    if (ids.size === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/.netlify/functions/checkout-cart-emails', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cartIds: Array.from(ids) }),
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const emails = data.emails || {};
        if (Object.keys(emails).length === 0) return;
        setCartEmails((prev) => {
          const next = new Map(prev);
          for (const [k, v] of Object.entries(emails)) next.set(k, v);
          return next;
        });
      } catch { /* swallow — row just won't show email */ }
    })();
    return () => { cancelled = true; };
  }, [stuckList, recentList, dropOffList]); // eslint-disable-line

  // ────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────
  if (!isAllowed) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-600)' }}>
        <AlertCircle size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
        <div>This page is restricted to Admin, IT, and Manager roles.</div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, minHeight: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Activity size={20} style={{ color: 'var(--navy)' }} />
          <h1 style={{ margin: 0, fontSize: 20, color: 'var(--navy)' }}>Telemetry</h1>
          <SurfaceToggle surface={surface} onChange={setSurface} />
          <EnvToggle env={env} onChange={setEnv} />
          <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
            {[
              { k: 'dashboard', label: 'Dashboard' },
              { k: 'summary', label: 'Summary' },
              { k: 'raw', label: 'Raw events' },
            ].map(o => (
              <button key={o.k} onClick={() => setView(o.k)}
                style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, border: 'none', background: view === o.k ? 'var(--gray-700)' : '#fff', color: view === o.k ? '#fff' : 'var(--gray-700)', cursor: 'pointer' }}>
                {o.label}
              </button>
            ))}
          </div>
          <StatusLine status={status} />
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {view === 'dashboard' && (
            <>
              <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
                {RANGES.map(r => (
                  <button key={r.id} onClick={() => setRangeId(r.id)}
                    style={{
                      padding: '6px 10px', fontSize: 12, fontWeight: 600, border: 'none',
                      background: rangeId === r.id ? 'var(--navy)' : '#fff',
                      color: rangeId === r.id ? '#fff' : 'var(--gray-700)', cursor: 'pointer',
                      borderRight: r.id !== 'custom' ? '1px solid var(--gray-200)' : 'none',
                    }}>
                    {r.label}
                  </button>
                ))}
              </div>
              {rangeId === 'custom' && (
                <input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
                  style={{ ...INPUT, fontSize: 12 }} />
              )}
            </>
          )}
          <button onClick={() => view === 'dashboard' ? fetchDashboard() : rawLoad()} disabled={status.state === 'polling'} style={BTN('var(--orange)', status.state === 'polling')}>
            <RefreshCw size={14} className={status.state === 'polling' ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Polling progress bar */}
      {status.state === 'polling' && (
        <div style={{ height: 2, background: 'var(--gray-100)', overflow: 'hidden', borderRadius: 2 }}>
          <div style={{ height: '100%', background: 'var(--navy)', animation: 'progress-indeterminate 1.5s ease-in-out infinite' }} />
        </div>
      )}

      {/* Errors */}
      {error && (() => {
        const isProdNotDeployed = env === 'prod'
          && error.status === 404
          && typeof error.body?.raw === 'string'
          && /Cannot GET \/api\/v1\/telemetry/.test(error.body.raw);
        if (isProdNotDeployed) {
          return (
            <div style={{ background: '#fff4e0', border: '1px solid #f5cf8c', color: '#7a4a00', padding: 12, borderRadius: 6, fontSize: 12.5 }}>
              <strong>Production endpoint not yet deployed.</strong> Click <strong>Sandbox-1</strong> above to view events.
            </div>
          );
        }
        return (
          <div style={{ background: '#fde6e6', border: '1px solid #f5b7b1', color: '#7a1818', padding: 10, borderRadius: 6, fontSize: 12.5 }}>
            <strong>Error {error.status}:</strong> <code style={{ wordBreak: 'break-all' }}>{JSON.stringify(error.body)}</code>
          </div>
        );
      })()}

      {view === 'dashboard' ? (
        surface === 'user' ? (
          <UserDashboardView
            user={userAgg}
            now={now}
            selectedSessionId={selectedSessionId}
            selectedSessionEvents={selectedSessionEvents}
            selectedLoading={selectedLoading}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            aiSummary={aiSummary}
            aiLoading={aiLoading}
            aiError={aiError}
            eventsExpanded={eventsExpanded}
            setEventsExpanded={setEventsExpanded}
            onSelectSession={drillSession}
            onClearSelection={() => { setSelectedSessionId(null); setSelectedSessionEvents([]); setAiSummary(null); setAiError(null); }}
          />
        ) : (
          <DashboardView
            agg={agg}
            recoveryRate={recoveryRate}
            stuckList={stuckList}
            recentList={recentList}
            dropOffList={dropOffList}
            completionsBySid={completionsBySid}
            completionsByCartId={completionsByCartId}
            callMatches={callMatches}
            cartEmails={cartEmails}
            history={history}
            now={now}
            env={env}
            selectedSessionId={selectedSessionId}
            selectedSessionEvents={selectedSessionEvents}
            selectedLoading={selectedLoading}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            aiSummary={aiSummary}
            aiLoading={aiLoading}
            aiError={aiError}
            eventsExpanded={eventsExpanded}
            setEventsExpanded={setEventsExpanded}
            onSelectSession={drillSession}
            onClearSelection={() => { setSelectedSessionId(null); setSelectedSessionEvents([]); setAiSummary(null); setAiError(null); }}
          />
        )
      ) : view === 'summary' ? (
        surface === 'user' ? (
          <div style={{ ...CARD, padding: '20px 24px', color: 'var(--gray-600)', fontSize: 13 }}>
            User Summary view is coming next. For now, switch to <strong>Dashboard</strong> for sign-in / create-account / password-reset friction, or <strong>Raw events</strong> for ad-hoc filtering.
          </div>
        ) : (
          <SummaryView agg={agg} range={range} customDate={customDate} />
        )
      ) : (
        <RawView
          events={events}
          since={rawSince} setSince={setRawSince}
          until={rawUntil} setUntil={setRawUntil}
          levels={rawLevels} setLevels={setRawLevels}
          eventFilter={rawEventFilter} setEventFilter={setRawEventFilter}
          sessionId={rawSessionId} setSessionId={setRawSessionId}
          cartId={rawCartId} setCartId={setRawCartId}
          orderId={rawOrderId} setOrderId={setRawOrderId}
          selectedId={rawSelectedId} setSelectedId={setRawSelectedId}
          onApply={rawLoad}
          onShowFullSession={drillSession}
        />
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes progress-indeterminate {
          0% { margin-left: -30%; width: 30%; }
          50% { margin-left: 30%; width: 30%; }
          100% { margin-left: 100%; width: 30%; }
        }
      `}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Dashboard view
// ───────────────────────────────────────────────────────────────────────────
function DashboardView({
  agg, recoveryRate, stuckList, recentList, dropOffList, completionsBySid, completionsByCartId, callMatches, cartEmails, history, now, env,
  selectedSessionId, selectedSessionEvents, selectedLoading, expandedRows, setExpandedRows,
  aiSummary, aiLoading, aiError, eventsExpanded, setEventsExpanded,
  onSelectSession, onClearSelection,
}) {
  const emailFor = (s) => s.email || (s.cartId ? cartEmails?.get(s.cartId) : null) || null;
  // Recovery alert
  const recoveryAlert =
    agg.recovered === 0 ? null :
    (agg.recovered >= 3 && recoveryRate >= 0.05) ? 'red' : 'yellow';

  return (
    <>
      {/* Recovery alert banner */}
      {recoveryAlert && (
        <div style={{
          background: recoveryAlert === 'red' ? '#fde6e6' : '#fff4e0',
          border: `1px solid ${recoveryAlert === 'red' ? '#f5b7b1' : '#f5cf8c'}`,
          color: recoveryAlert === 'red' ? '#7a1818' : '#7a4a00',
          padding: 10, borderRadius: 6, fontSize: 12.5,
        }}>
          {recoveryAlert === 'red'
            ? <span><strong>⚠ {agg.recovered} stuck-state recoveries</strong> ({(recoveryRate * 100).toFixed(0)}% of {agg.attempted} place-order sessions) — customers are bouncing through /cart to recover</span>
            : <span><strong>{agg.recovered} stuck-state recovery{agg.recovered > 1 ? 'ies' : ''}</strong> — customers bounced through /cart to recover</span>}
        </div>
      )}

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <Tile
          label="Attempted"
          value={agg.attempted}
          sub={`clicked Place Order · of ${agg.visitingSessions} visiting`}
          color="blue"
        />
        <Tile
          label="Completed"
          value={agg.completed}
          sub={agg.completed > 0 ? `unique orders` : '—'}
          color="green"
        />
        <Tile
          label="Errored"
          value={agg.errored}
          sub={agg.errored > 0 ? 'no success, no recovery' : 'no errors'}
          color={colorErrored(agg.errored)}
        />
        <Tile
          label="Stuck"
          value={agg.stuck}
          sub={agg.stuck > 0 ? `idle ≥ ${STUCK_THRESHOLD_MS / 1000}s` : 'none stuck'}
          color={colorStuck(agg.stuck)}
        />
        <Tile
          label="Recovered"
          value={agg.recovered}
          sub={agg.attempted > 0 ? `${(recoveryRate * 100).toFixed(0)}% of attempts` : '—'}
          color={colorRecovered(agg.recovered, recoveryRate)}
        />
        <Tile
          label="Conversion"
          value={agg.conversion == null ? '—' : `${(agg.conversion * 100).toFixed(0)}%`}
          sub={agg.effectiveAttempts >= 5 ? `of ${agg.effectiveAttempts} effective attempts` : `n=${agg.effectiveAttempts} (need ≥ 5)`}
          color={colorConversion(agg.conversion)}
        />
      </div>

      {/* Deploy Quality card */}
      <div style={{ ...CARD, padding: '4px 0' }}>
        <div style={{ padding: '6px 14px 0', fontSize: 10.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Deploy Quality
        </div>
        <div style={{ display: 'flex', gap: 0 }}>
          <StatBlock
            label="Concurrency Guard"
            value={`${agg.aborts} / ${agg.invokes}`}
            sub={agg.invokes > 0 ? `${((agg.aborts / agg.invokes) * 100).toFixed(0)}% abort rate (race-guarded)` : 'no shipping clicks yet'}
            color={agg.invokes > 0 ? colorAbortRate(agg.aborts / agg.invokes) : 'muted'}
          />
          <StatBlock
            label="Deploy Health"
            value={agg.divergent}
            sub={agg.divergent === 0 ? 'no shippingDivergence' : 'preSubmit divergence detected'}
            color={colorDivergent(agg.divergent)}
          />
        </div>
      </div>

      {/* Three lists row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
        <SessionList
          title={`Stuck Checkouts (${stuckList.length})`}
          sessions={stuckList}
          renderRow={(s) => (
            <>
              <StatusBadge status={s.status} />
              <div style={{ fontSize: 11.5, color: 'var(--gray-800)', fontWeight: 600, marginTop: 4 }}>
                {s.customer || '(no name yet)'} {s.poCount > 1 && <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}>· retried {s.poCount}×</span>}
              </div>
              {s.place && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1 }}>{s.place}</div>}
              {emailFor(s) && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1, wordBreak: 'break-all' }}>{emailFor(s)}</div>}
              {s.errorMessage && (
                <div style={{ fontSize: 10.5, color: '#a51d1d', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.errorMessage.slice(0, 80)}
                </div>
              )}
              <CompletedLaterBadge session={s} sidMap={completionsBySid} cartMap={completionsByCartId} />
              <CsCallBadge session={s} callMatches={callMatches} />
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace' }}>
                {s.cartId ? `cart-${s.cartId.slice(-6)}` : `sess-${s.sessionId.slice(-8)}`} · {fmtAgo(s.lastTs, now)}
              </div>
            </>
          )}
          selectedId={selectedSessionId}
          onSelect={onSelectSession}
          empty="No stuck checkouts in this window."
        />

        <SessionList
          title={`Recent Checkouts (${recentList.length})`}
          sessions={recentList}
          renderRow={(s) => (
            <>
              <StatusBadge status={s.status} />
              <div style={{ fontSize: 11.5, color: 'var(--gray-800)', fontWeight: 600, marginTop: 4 }}>
                {s.customer || '(no name yet)'} {s.orderId && <span style={{ fontFamily: '"SF Mono", Menlo, monospace', color: 'var(--gray-500)', fontWeight: 400 }}>· #{s.orderId}</span>}
              </div>
              {s.place && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1 }}>{s.place}</div>}
              {emailFor(s) && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1, wordBreak: 'break-all' }}>{emailFor(s)}</div>}
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace' }}>
                {!s.orderId && (s.cartId ? `cart-${s.cartId.slice(-6)} · ` : `sess-${s.sessionId.slice(-8)} · `)}{fmtAgo(s.lastTs, now)}
              </div>
            </>
          )}
          selectedId={selectedSessionId}
          onSelect={onSelectSession}
          empty="No place-order attempts in this window."
        />

        <SessionList
          title={`Drop-Offs (${dropOffList.length}${agg.dropOffSessions.length > 50 ? ` of ${agg.dropOffSessions.length}` : ''})`}
          sessions={dropOffList}
          renderRow={(s) => (
            <>
              <StageBadge stage={s.stage} />
              <div style={{ fontSize: 11.5, color: 'var(--gray-800)', fontWeight: 600, marginTop: 4 }}>
                {s.customer || '(no name yet)'}
              </div>
              {s.place && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1 }}>{s.place}</div>}
              {emailFor(s) && <div style={{ fontSize: 10.5, color: 'var(--gray-600)', marginTop: 1, wordBreak: 'break-all' }}>{emailFor(s)}</div>}
              <CompletedLaterBadge session={s} sidMap={completionsBySid} cartMap={completionsByCartId} />
              <CsCallBadge session={s} callMatches={callMatches} />
              <div style={{ fontSize: 10.5, color: 'var(--gray-500)', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace' }}>
                {s.cartId ? `cart-${s.cartId.slice(-6)} · ` : `sess-${s.sessionId.slice(-8)} · `}{fmtAgo(s.lastTs, now)}
              </div>
            </>
          )}
          selectedId={selectedSessionId}
          onSelect={onSelectSession}
          empty="No drop-offs in this window."
        />
      </div>

      {/* Session detail pane — AI summary first, raw log collapsed by default */}
      {selectedSessionId && (
        <div style={{ ...CARD }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--gray-50)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Selected checkout</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace' }}>
                {selectedSessionId}{selectedLoading && ' · Loading events…'}
                {!selectedLoading && selectedSessionEvents.length > 0 && ` · ${selectedSessionEvents.length} events`}
              </div>
            </div>
            <button onClick={onClearSelection} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--gray-500)' }}>✕</button>
          </div>

          {/* AI summary card */}
          <AiSummaryCard summary={aiSummary} loading={aiLoading} error={aiError} eventCount={selectedSessionEvents.length} />

          {/* Raw events log — collapsed by default */}
          {selectedSessionEvents.length > 0 && (
            <div style={{ borderTop: '1px solid var(--gray-200)' }}>
              <button onClick={() => setEventsExpanded(v => !v)}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 14px',
                  background: '#fff', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 12, fontWeight: 600, color: 'var(--gray-700)',
                  fontFamily: 'inherit',
                }}>
                {eventsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {eventsExpanded ? 'Hide' : 'Show'} raw event log ({selectedSessionEvents.length} events)
              </button>
              {eventsExpanded && (
                <div style={{ maxHeight: 480, overflowY: 'auto', borderTop: '1px solid var(--gray-100)' }}>
                  {selectedSessionEvents.map((e, i) => (
                    <EventRow
                      key={e._id || i}
                      event={e}
                      expanded={!!expandedRows[e._id || i]}
                      onToggle={() => setExpandedRows(p => ({ ...p, [e._id || i]: !p[e._id || i] }))}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* History per-day funnel */}
      {history && history.length > 0 && (
        <div style={{ ...CARD }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
            Last {HISTORY_DAYS} days · per-day funnel
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Day (UTC)</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Place-Order</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Orders</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Conv</th>
                </tr>
              </thead>
              <tbody>
                {history.map(row => {
                  const conv = row.attempted > 0 ? row.completed / row.attempted : null;
                  const todayUtc = new Date().toISOString().slice(0, 10);
                  const isToday = row.day === todayUtc;
                  return (
                    <tr key={row.day} style={{ background: isToday ? 'var(--gray-50)' : 'transparent', cursor: 'pointer' }} onClick={() => null /* TODO: jump to that day */}>
                      <td style={{ ...TD, fontFamily: '"SF Mono", Menlo, monospace' }}>{row.day}{isToday && ' · today'}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.attempted}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.completed}</td>
                      <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: conv == null ? 'var(--gray-400)' : conv >= 0.85 ? '#1a8a4a' : conv >= 0.7 ? '#a06000' : '#a51d1d' }}>
                        {conv == null ? '—' : `${(conv * 100).toFixed(0)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sum check (developer-visible footer) */}
      <div style={{ fontSize: 10.5, color: 'var(--gray-400)', textAlign: 'right', fontFamily: '"SF Mono", Menlo, monospace' }}>
        attempted={agg.attempted} = completed({agg.completed}) + inFlight({agg.inFlight}) + errored({agg.errored}) + stuck({agg.stuck}) + recovered({agg.recovered}){' '}
        {agg.completed + agg.inFlight + agg.errored + agg.stuck + agg.recovered === agg.attempted ? '✓' : '⚠ MISMATCH'}
      </div>
    </>
  );
}

function SessionList({ title, sessions, renderRow, selectedId, onSelect, empty }) {
  return (
    <div style={{ ...CARD, display: 'flex', flexDirection: 'column', maxHeight: 460, minHeight: 200 }}>
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--gray-200)', fontSize: 11.5, fontWeight: 700, color: 'var(--gray-700)', textTransform: 'uppercase', letterSpacing: 0.4, background: 'var(--gray-50)' }}>
        {title}
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {sessions.length === 0 ? (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-400)', fontStyle: 'italic' }}>{empty}</div>
        ) : (
          sessions.map(s => (
            <div key={s.sessionId}
              onClick={() => onSelect(s.sessionId)}
              style={{
                padding: '8px 12px',
                borderBottom: '1px solid var(--gray-100)',
                cursor: 'pointer',
                background: selectedId === s.sessionId ? '#e6f1fa' : 'transparent',
              }}>
              {renderRow(s)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Raw events view (the original page, preserved for ad-hoc filter / search)
// ───────────────────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────────────────
// Summary view — period roll-up + benchmark comparison
// ───────────────────────────────────────────────────────────────────────────
// GA4 funnel benchmark bands. Higher is better, opposite of the failure-mode
// SUMMARY_BENCHMARKS — these get their own status logic.
//   visit_to_cart        — sessions → add_to_cart. Industry 5-10%.
//   cart_to_purchase     — add_to_cart → purchase. THE rate Greg asked about.
//                          Industry 35-50% for typical e-com; food/specialty
//                          can vary 30-60%.
//   visit_to_purchase    — overall site CR. Industry 1-3% e-com, 2-4%
//                          higher-AOV food/specialty.
const GA4_BENCHMARKS = {
  visit_to_cart:     { good: 0.07, watch: 0.04, label: 'Visit → Cart',          note: 'Industry 5-10%. Lower: PDP / catalog isn\'t converting browsers to interest.' },
  cart_to_purchase:  { good: 0.35, watch: 0.20, label: 'Cart → Order',          note: 'Industry 35-50% for typical e-com. Crab/perishable can run lower because of date-of-delivery friction. The headline funnel rate.' },
  visit_to_purchase: { good: 0.025, watch: 0.012, label: 'Visit → Order (CR)', note: 'Industry 1-3% e-com, 2-4% higher-AOV food/specialty. Overall site conversion.' },
};

function ga4Status(rate, band) {
  if (rate == null) return 'muted';
  if (rate >= band.good) return 'green';
  if (rate >= band.watch) return 'yellow';
  return 'red';
}

// Pick the GA4 query params for the current Checkout-Telemetry range.
// GA4's smallest grain is one day; rolling-hour ranges fall back to "today".
function ga4ParamsForRange(range, customDate) {
  if (range.id === 'yesterday') {
    const d = ga4LocalDate(-1);
    return { startDate: d, endDate: d };
  }
  if (range.id === 'custom' && customDate) {
    return { startDate: customDate, endDate: customDate };
  }
  // Live (4h) / 24h / Today UTC → use GA4 "today" (property TZ = America/New_York,
  // close enough; the displayed comparison is directional, not exact)
  return { days: 1 };
}

// Local-timezone YYYY-MM-DD (toISOString is UTC and rolls to tomorrow after 8 PM ET)
function ga4LocalDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// GA4 card's own range presets — independent of the page's telemetry range,
// so multi-day GA4 windows are viewable without reloading telemetry events.
const GA4_RANGES = [
  { id: 'page',      label: 'Follow page range' },
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d',        label: 'Last 7 days' },
  { id: '14d',       label: 'Last 14 days' },
  { id: '30d',       label: 'Last 30 days' },
  { id: 'custom',    label: 'Custom range…' },
];

function ga4ParamsForSelection(sel, start, end, range, customDate) {
  if (sel === 'today') return { days: 1 };
  if (sel === 'yesterday') { const d = ga4LocalDate(-1); return { startDate: d, endDate: d }; }
  if (sel === '7d')  return { startDate: ga4LocalDate(-6),  endDate: ga4LocalDate(0) };
  if (sel === '14d') return { startDate: ga4LocalDate(-13), endDate: ga4LocalDate(0) };
  if (sel === '30d') return { startDate: ga4LocalDate(-29), endDate: ga4LocalDate(0) };
  if (sel === 'custom') {
    if (!start || !end) return null; // wait for both dates
    return start <= end ? { startDate: start, endDate: end } : { startDate: end, endDate: start };
  }
  return ga4ParamsForRange(range, customDate);
}

function SummaryView({ agg, range, customDate }) {
  const summary = useMemo(() => computeSummary(agg), [agg]);
  const flagged = summary.rows.filter(r => r.status === 'red');
  const watch   = summary.rows.filter(r => r.status === 'yellow');

  // GA4 funnel — fetched async, rendered when available. Has its own range
  // selector ('page' = mirror the page's telemetry range, the old behavior).
  const [ga4, setGa4] = useState(null);
  const [ga4Loading, setGa4Loading] = useState(false);
  const [ga4Error, setGa4Error] = useState(null);
  const [ga4RangeId, setGa4RangeId] = useState('page');
  const [ga4Start, setGa4Start] = useState('');
  const [ga4End, setGa4End] = useState('');
  useEffect(() => {
    let cancelled = false;
    async function load() {
      const params = ga4ParamsForSelection(ga4RangeId, ga4Start, ga4End, range, customDate);
      if (!params) return; // custom range with dates not yet picked — keep current data
      setGa4Loading(true);
      setGa4Error(null);
      const qs = new URLSearchParams(params).toString();
      try {
        const res = await fetch('/.netlify/functions/ga4-funnel?' + qs);
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch (_) { data = { error: 'non-json', raw: text.slice(0, 200) }; }
        if (cancelled) return;
        if (res.status !== 200) setGa4Error({ status: res.status, body: data });
        else setGa4(data);
      } catch (e) {
        if (!cancelled) setGa4Error({ status: 0, body: { error: e.message } });
      } finally {
        if (!cancelled) setGa4Loading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [range.id, customDate, ga4RangeId, ga4Start, ga4End]); // eslint-disable-line

  // Top-line outcome split (sums to visiting sessions)
  const visiting = agg.visitingSessions;
  const outcomes = [
    { key: 'success',         label: 'Completed',          count: summary.buckets.success,         color: 'green' },
    { key: 'recovered',       label: 'Recovered (via /cart)', count: summary.buckets.recovered,    color: 'yellow' },
    { key: 'card_declined',   label: 'Card declined',      count: summary.buckets.card_declined,   color: 'red' },
    { key: 'avs_error',       label: 'AVS / address',      count: summary.buckets.avs_error,       color: 'red' },
    { key: 'turnstile_fail',  label: 'Turnstile failed',   count: summary.buckets.turnstile_fail,  color: 'red' },
    { key: 'submit_other',    label: 'Other submit error', count: summary.buckets.submit_other,    color: 'red' },
    { key: 'stuck',           label: 'Stuck',              count: summary.buckets.stuck,           color: 'yellow' },
    { key: 'walked_away',     label: 'Walked away',        count: summary.buckets.walked_away,     color: 'muted' },
    { key: 'payment_drop',    label: 'Drop @ Payment',     count: summary.buckets.payment_drop,    color: 'yellow' },
    { key: 'shipping_drop',   label: 'Drop @ Shipping',    count: summary.buckets.shipping_drop,   color: 'muted' },
    { key: 'initial_drop',    label: 'Drop @ Initial',     count: summary.buckets.initial_drop,    color: 'muted' },
  ];
  const totalAccountedFor = outcomes.reduce((acc, o) => acc + o.count, 0);

  const rangeLabel = range.label + (range.id === 'custom' && customDate ? ` (${customDate})` : '');

  return (
    <>
      {/* Period header */}
      <div style={{ ...CARD, padding: '12px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Period summary
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginTop: 2 }}>
          {rangeLabel} · {visiting} visiting sessions · {agg.attempted} clicked Place Order · {agg.completed} completed
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4 }}>
          Conversion (recovery-adjusted): {agg.conversion == null ? '—' : `${(agg.conversion * 100).toFixed(0)}%`}
          {agg.effectiveAttempts < 5 && <span style={{ color: 'var(--gray-400)' }}> · low n, treat as directional</span>}
        </div>
      </div>

      {/* GA4 funnel card — Add-to-Cart → Order benchmark */}
      <div style={{ ...CARD, padding: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Cart → Order funnel (GA4)</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={ga4RangeId}
              onChange={e => setGa4RangeId(e.target.value)}
              style={{ fontSize: 11, padding: '3px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, color: 'var(--navy)', background: '#fff' }}
            >
              {GA4_RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            {ga4RangeId === 'custom' && (
              <>
                <input type="date" value={ga4Start} max={ga4LocalDate(0)} onChange={e => setGa4Start(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 4px', border: '1px solid var(--gray-300)', borderRadius: 4 }} />
                <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>→</span>
                <input type="date" value={ga4End} max={ga4LocalDate(0)} onChange={e => setGa4End(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 4px', border: '1px solid var(--gray-300)', borderRadius: 4 }} />
              </>
            )}
            <span style={{ fontSize: 10.5, color: 'var(--gray-500)' }}>
              {ga4Loading && 'Loading from crabplace-ga4…'}
              {!ga4Loading && ga4 && ga4.date_range && `${ga4.date_range.startDate} → ${ga4.date_range.endDate}`}
              {ga4Error && <span style={{ color: '#a51d1d' }}>error {ga4Error.status}</span>}
            </span>
          </span>
        </div>
        {ga4Loading && (
          <div style={{ padding: 14, fontSize: 12, color: 'var(--gray-500)' }}>Loading…</div>
        )}
        {ga4Error && (
          <div style={{ padding: 14, fontSize: 12, color: '#a51d1d' }}>
            GA4 fetch failed: <code style={{ wordBreak: 'break-all' }}>{JSON.stringify(ga4Error.body).slice(0, 200)}</code>
          </div>
        )}
        {ga4 && !ga4Loading && (
          <div style={{ padding: 14 }}>
            {/* Raw counts */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
              {[
                { label: 'Sessions',        v: ga4.sessions },
                { label: 'Add-to-carts',    v: ga4.add_to_carts },
                { label: 'Checkouts',       v: ga4.checkouts },
                { label: 'Purchases',       v: ga4.transactions },
                { label: 'Revenue',         v: '$' + (ga4.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }) },
              ].map(s => (
                <div key={s.label} style={{ padding: '6px 10px', background: 'var(--gray-50)', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{s.label}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{typeof s.v === 'number' ? s.v.toLocaleString() : s.v}</div>
                </div>
              ))}
            </div>
            {/* Funnel ratios with bands */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>Funnel rate</th>
                  <th style={{ ...TH, textAlign: 'right' }}>This period</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Healthy ≥</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Watch ≥</th>
                  <th style={TH}>Status</th>
                  <th style={TH}>Industry note</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(GA4_BENCHMARKS).map(([key, band]) => {
                  const r = ga4.ratios?.[key];
                  const status = ga4Status(r, band);
                  const sevColor = TILE_COLORS[status] || TILE_COLORS.muted;
                  return (
                    <tr key={key} style={{ background: status === 'red' ? '#fef5f5' : status === 'yellow' ? '#fffbf2' : 'transparent' }}>
                      <td style={{ ...TD, fontWeight: 600 }}>{band.label}</td>
                      <td style={{ ...TD, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: sevColor.fg }}>
                        {r == null ? '—' : `${(r * 100).toFixed(r < 0.05 ? 2 : 1)}%`}
                      </td>
                      <td style={{ ...TD, textAlign: 'right', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                        {(band.good * 100).toFixed(band.good < 0.05 ? 1 : 0)}%
                      </td>
                      <td style={{ ...TD, textAlign: 'right', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                        {(band.watch * 100).toFixed(band.watch < 0.05 ? 1 : 0)}%
                      </td>
                      <td style={TD}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                          background: sevColor.bg, color: sevColor.fg, textTransform: 'uppercase',
                        }}>
                          {status === 'red' ? 'Below normal' : status === 'yellow' ? 'Watch' : status === 'green' ? 'OK' : '—'}
                        </span>
                      </td>
                      <td style={{ ...TD, fontSize: 11, color: 'var(--gray-600)', maxWidth: 380 }}>{band.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--gray-500)', lineHeight: 1.5 }}>
              GA4 grain is one day (property TZ = America/New_York). "Follow page range" mirrors the telemetry range above (Live / 24h / Today → today); or pick a preset / custom range in the card header for a wider GA4 window. <strong>Higher is better</strong> for these rates (opposite of the failure-mode benchmarks below).
            </div>
          </div>
        )}
      </div>

      {/* Attention banner */}
      {flagged.length > 0 && (
        <div style={{ background: '#fde6e6', border: '1px solid #f5b7b1', color: '#7a1818', padding: 12, borderRadius: 6, fontSize: 12.5 }}>
          <strong>⚠ {flagged.length} metric{flagged.length > 1 ? 's' : ''} above the warning band</strong> for this period:&nbsp;
          {flagged.map(r => r.label).join(' · ')}
          <div style={{ marginTop: 6, fontSize: 11.5, color: '#5a0e0e' }}>
            See the table below for benchmark context. Industry bands are conservative starting points — tune as we learn what's normal here.
          </div>
        </div>
      )}
      {flagged.length === 0 && watch.length > 0 && (
        <div style={{ background: '#fff4e0', border: '1px solid #f5cf8c', color: '#7a4a00', padding: 10, borderRadius: 6, fontSize: 12.5 }}>
          {watch.length} metric{watch.length > 1 ? 's' : ''} in the watch zone (above the "good" band but under "warn"): {watch.map(r => r.label).join(' · ')}
        </div>
      )}
      {flagged.length === 0 && watch.length === 0 && totalAccountedFor > 0 && (
        <div style={{ background: '#e6f4ea', border: '1px solid #b6dec6', color: '#1a5a32', padding: 10, borderRadius: 6, fontSize: 12.5 }}>
          ✓ All metrics in this period are inside their healthy bands.
        </div>
      )}

      {/* Outcome breakdown card — shows where every visiting session ended up */}
      <div style={{ ...CARD, padding: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
          Where {visiting} visiting sessions ended up
        </div>
        <div style={{ padding: 14 }}>
          {visiting === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--gray-500)', fontStyle: 'italic' }}>No sessions in this window.</div>
          ) : (
            <>
              {/* Stacked horizontal bar */}
              <div style={{ display: 'flex', height: 22, borderRadius: 4, overflow: 'hidden', border: '1px solid var(--gray-200)' }}>
                {outcomes.filter(o => o.count > 0).map(o => {
                  const pct = (o.count / visiting) * 100;
                  const c = TILE_COLORS[o.color];
                  return (
                    <div key={o.key} title={`${o.label}: ${o.count} (${pct.toFixed(1)}%)`}
                      style={{ width: pct + '%', background: c.fg, opacity: o.color === 'muted' ? 0.35 : 0.85 }} />
                  );
                })}
              </div>
              {/* Legend table */}
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
                {outcomes.filter(o => o.count > 0).map(o => {
                  const pct = (o.count / visiting) * 100;
                  const c = TILE_COLORS[o.color];
                  return (
                    <div key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <span style={{ width: 10, height: 10, background: c.fg, opacity: o.color === 'muted' ? 0.35 : 0.85, borderRadius: 2, flexShrink: 0 }} />
                      <span style={{ color: 'var(--gray-700)', flex: 1 }}>{o.label}</span>
                      <span style={{ color: 'var(--gray-800)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{o.count}</span>
                      <span style={{ color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums', minWidth: 50, textAlign: 'right' }}>{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Benchmark table */}
      <div style={{ ...CARD, padding: 0 }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', fontSize: 13, fontWeight: 700, color: 'var(--navy)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Metrics vs industry bands</span>
          <span style={{ fontSize: 10.5, fontWeight: 400, color: 'var(--gray-500)' }}>
            Hover the row note column for context · denominators differ per row
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Metric</th>
                <th style={{ ...TH, textAlign: 'right' }}>Count</th>
                <th style={{ ...TH, textAlign: 'right' }}>Rate</th>
                <th style={{ ...TH, textAlign: 'right' }}>Healthy ≤</th>
                <th style={{ ...TH, textAlign: 'right' }}>Warn at</th>
                <th style={TH}>Status</th>
                <th style={TH}>Basis</th>
                <th style={TH}>Industry band & note</th>
              </tr>
            </thead>
            <tbody>
              {summary.rows.map(r => {
                const sevColor = r.status === 'red' ? TILE_COLORS.red : r.status === 'yellow' ? TILE_COLORS.yellow : TILE_COLORS.green;
                return (
                  <tr key={r.key} style={{ background: r.status === 'red' ? '#fef5f5' : r.status === 'yellow' ? '#fffbf2' : 'transparent' }}>
                    <td style={{ ...TD, fontWeight: 600, color: 'var(--gray-800)' }}>{r.label}</td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count}</td>
                    <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: sevColor.fg }}>
                      {r.ratio == null ? '—' : `${(r.ratio * 100).toFixed(1)}%`}
                    </td>
                    <td style={{ ...TD, textAlign: 'right', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                      {(r.good * 100).toFixed(r.good < 0.01 ? 1 : 0)}%
                    </td>
                    <td style={{ ...TD, textAlign: 'right', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                      {(r.warn * 100).toFixed(r.warn < 0.01 ? 1 : 0)}%
                    </td>
                    <td style={TD}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                        background: sevColor.bg, color: sevColor.fg, textTransform: 'uppercase',
                      }}>
                        {r.status === 'red' ? 'Way off' : r.status === 'yellow' ? 'Watch' : 'OK'}
                      </span>
                    </td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--gray-500)' }}>
                      {r.basis === 'attempted' ? '/ attempted' : r.basis === 'visiting' ? '/ visiting' : r.basis === 'invokes' ? '/ invokes' : ''}
                    </td>
                    <td style={{ ...TD, fontSize: 11, color: 'var(--gray-600)', maxWidth: 360 }}>{r.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Method note */}
      <div style={{ fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.6, padding: '6px 4px' }}>
        <strong>Method:</strong> outcome buckets are mutually exclusive — every visiting session falls into exactly one. Failure-mode rates use <em>attempted</em> (sessions that clicked Place Order) as the denominator; drop-off rates use <em>visiting</em> (all sessions). Industry bands are conservative starting points (Baymard 2024 e-commerce panel + BC headless typical ranges); tune the constants in <code style={{ fontSize: 10.5 }}>SUMMARY_BENCHMARKS</code> as we learn what's normal here.
      </div>
    </>
  );
}

function RawView({ events, since, setSince, until, setUntil, levels, setLevels, eventFilter, setEventFilter, sessionId, setSessionId, cartId, setCartId, orderId, setOrderId, selectedId, setSelectedId, onApply, onShowFullSession }) {
  const selected = useMemo(() => events.find(r => (r._id || r.timestamp + ':' + r.event) === selectedId) || null, [events, selectedId]);

  const toggleLevel = (lvl) => {
    setLevels(prev => {
      const next = new Set(prev);
      if (next.has(lvl)) next.delete(lvl); else next.add(lvl);
      if (next.size === 0) next.add(lvl);
      return next;
    });
  };

  return (
    <>
      <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, alignItems: 'end' }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Since</label>
          <input type="date" value={since} onChange={e => setSince(e.target.value)} style={{ ...INPUT, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Until (optional)</label>
          <input type="date" value={until} onChange={e => setUntil(e.target.value)} style={{ ...INPUT, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Level</label>
          <div style={{ display: 'flex', gap: 4 }}>
            {['error', 'warn', 'info'].map(lvl => (
              <span key={lvl} onClick={() => toggleLevel(lvl)} style={PILL(levels.has(lvl))}>{lvl}</span>
            ))}
          </div>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Event substring</label>
          <input type="text" value={eventFilter} onChange={e => setEventFilter(e.target.value)} style={{ ...INPUT, width: '100%' }} placeholder="e.g. preSubmit" />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Session ID</label>
          <input type="text" value={sessionId} onChange={e => setSessionId(e.target.value)} style={{ ...INPUT, width: '100%' }} placeholder="tlm-…" />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Cart ID</label>
          <input type="text" value={cartId} onChange={e => setCartId(e.target.value)} style={{ ...INPUT, width: '100%' }} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Order ID</label>
          <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)} style={{ ...INPUT, width: '100%' }} />
        </div>
        <button onClick={onApply} style={{ ...BTN('var(--navy)'), width: '100%', justifyContent: 'center' }}>
          <Search size={13} /> Apply filters
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 12, flex: 1, minHeight: 0 }}>
        <div style={{ ...CARD, overflow: 'auto', maxHeight: 600 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Time</th>
                <th style={TH}>Lvl</th>
                <th style={TH}>Event</th>
                <th style={TH}>Session</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={4} style={{ ...TD, textAlign: 'center', color: 'var(--gray-500)', padding: 24 }}>No events.</td></tr>
              )}
              {events.map((r, i) => {
                const id = r._id || (r.timestamp + ':' + r.event + ':' + (r.sessionId || ''));
                const isSel = selectedId === id;
                const lvl = eventLevel(r.event);
                return (
                  <tr key={id} onClick={() => setSelectedId(isSel ? null : id)}
                    style={{ cursor: 'pointer', background: isSel ? '#e6f1fa' : 'transparent' }}>
                    <td style={{ ...TD, fontFamily: '"SF Mono", Menlo, monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtTs(r.timestamp)}</td>
                    <td style={TD}><LevelPill level={lvl} /></td>
                    <td style={{ ...TD, fontFamily: '"SF Mono", Menlo, monospace', fontSize: 11 }}>{r.event}</td>
                    <td style={{ ...TD, fontFamily: '"SF Mono", Menlo, monospace', fontSize: 10.5, color: 'var(--gray-500)' }}>{(r.sessionId || '').slice(-12)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selected && (
          <div style={{ ...CARD, padding: 14, overflow: 'auto', maxHeight: 600 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{friendlyName(selected.event)}</div>
                <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginTop: 2 }}>
                  <LevelPill level={eventLevel(selected.event)} /> · {fmtTs(selected.timestamp)} · <code>{selected.sessionId}</code>
                </div>
              </div>
              <button onClick={() => onShowFullSession(selected.sessionId)} style={BTN('var(--navy)')}>Show full session</button>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>URL</div>
            <div style={{ ...CODE, marginBottom: 10 }}>{selected.url}</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>Data</div>
            <div style={CODE}>{JSON.stringify(selected.data, null, 2)}</div>
          </div>
        )}
      </div>
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// User (auth) telemetry — sign-in / create-account / password-reset funnels.
//
// Classification is by event NAME, not by source — checkout-embedded auth
// events (customer.signin.*, customer.create.*, customer.email-signin.*,
// customer.guest-signup.*) belong under User even though their source is
// `tcp-checkout`. Storefront auth events (signin.modal.*, create-account.*,
// auth.page.*) come in as `tcp-theme`.
// ───────────────────────────────────────────────────────────────────────────

function authFunnel(eventName) {
  if (!eventName) return null;
  if (/^signin\.modal\./.test(eventName)) return 'signin';
  if (/^auth\.page\.login\./.test(eventName)) return 'signin';
  if (/^customer\.signin\./.test(eventName)) return 'signin';
  if (/^customer\.email-signin\./.test(eventName)) return 'signin';
  if (/^create-account\./.test(eventName)) return 'create-account';
  if (/^auth\.create-account\./.test(eventName)) return 'create-account';
  if (/^customer\.create\./.test(eventName)) return 'create-account';
  if (/^customer\.guest-signup\./.test(eventName)) return 'create-account';
  if (/^auth\.page\.forgot\./.test(eventName)) return 'password-reset';
  if (/^auth\.page\.newpassword\./.test(eventName)) return 'password-reset';
  if (/^forgot-password\./.test(eventName)) return 'password-reset';
  if (/^new-password\./.test(eventName)) return 'password-reset';
  return null;
}

function authSurface(eventName) {
  if (!eventName) return null;
  if (/^signin\.modal\./.test(eventName))          return 'modal';
  if (/^auth\.page\.login\./.test(eventName))      return '/login.php';
  if (/^customer\.signin\./.test(eventName))       return 'checkout';
  if (/^customer\.email-signin\./.test(eventName)) return 'checkout-email';
  if (/^create-account\./.test(eventName))         return 'storefront';
  if (/^auth\.create-account\./.test(eventName))   return 'storefront';
  if (/^customer\.create\./.test(eventName))       return 'checkout';
  if (/^customer\.guest-signup\./.test(eventName)) return 'guest-signup';
  if (/^auth\.page\.forgot\./.test(eventName))     return 'forgot-password';
  if (/^auth\.page\.newpassword\./.test(eventName))return 'new-password';
  return null;
}

// 'attempt' | 'success' | 'failure' | 'lifecycle'
function classifyAuthEvent(eventName) {
  if (!eventName) return null;
  if (/\.success(\.|$)/.test(eventName)) return 'success';
  if (/(\.error|\.failed|\.thrown|\.empty|\.server\.error)(\.|$)/.test(eventName)) return 'failure';
  if (/(\.attempt|\.submit)(\.|$)/.test(eventName)) return 'attempt';
  return 'lifecycle';
}

const USER_FUNNELS = ['signin', 'create-account', 'password-reset'];
const FUNNEL_LABELS = {
  'signin':         'Sign-in',
  'create-account': 'Create-account',
  'password-reset': 'Password reset',
};

function extractAuthIdentity(data, target) {
  if (!data || typeof data !== 'object') return;
  if (!target.customer && typeof data.email === 'string' && data.email.includes('@')) {
    target.customer = data.email;
  }
  if (!target.customer && typeof data.emailDomain === 'string') {
    target.customer = '*@' + data.emailDomain;
  }
}

function computeUserAggregates(events, nowMs) {
  const byFunnel = {};
  for (const f of USER_FUNNELS) byFunnel[f] = new Map();

  for (const e of events) {
    const sid = e.sessionId;
    if (!sid) continue;
    const funnel = authFunnel(e.event);
    if (!funnel) continue;

    let row = byFunnel[funnel].get(sid);
    if (!row) {
      row = {
        sessionId: sid, lastTs: 0,
        hasAttempt: false, hasSuccess: false, hasFailure: false,
        errorMessage: null, surface: null,
        customer: '', cartId: null,
      };
      byFunnel[funnel].set(sid, row);
    }

    if (e.timestamp && e.timestamp > row.lastTs) row.lastTs = e.timestamp;
    if (!row.surface) {
      const s = authSurface(e.event);
      if (s) row.surface = s;
    }

    const cls = classifyAuthEvent(e.event);
    if (cls === 'attempt')  row.hasAttempt = true;
    if (cls === 'success')  row.hasSuccess = true;
    if (cls === 'failure') {
      row.hasFailure = true;
      row.errorMessage = row.errorMessage
        || e.data?.errorMessage
        || e.data?.message
        || e.data?.error
        || e.event;
    }

    if (e.data?.cartId && !row.cartId) row.cartId = e.data.cartId;
    extractAuthIdentity(e.data, row);
  }

  const result = {};
  for (const f of USER_FUNNELS) {
    let attempted = 0, succeeded = 0, failed = 0, stuck = 0, inFlight = 0;
    const stuckList = [], failureList = [];
    const surfaceCounts = new Map();
    for (const row of byFunnel[f].values()) {
      if (!row.hasAttempt) continue;
      attempted++;
      if (row.surface) {
        surfaceCounts.set(row.surface, (surfaceCounts.get(row.surface) || 0) + 1);
      }
      if (row.hasSuccess) {
        succeeded++;
      } else if (row.hasFailure) {
        failed++;
        failureList.push(row);
      } else {
        const idleMs = nowMs - row.lastTs;
        if (idleMs >= STUCK_THRESHOLD_MS) {
          stuck++;
          stuckList.push(row);
        } else {
          inFlight++;
        }
      }
    }
    result[f] = {
      label: FUNNEL_LABELS[f],
      attempted, succeeded, failed, stuck, inFlight,
      successRate: attempted > 0 ? succeeded / attempted : null,
      surfaceCounts: [...surfaceCounts.entries()].sort((a, b) => b[1] - a[1]),
      stuckList: stuckList.sort((a, b) => b.lastTs - a.lastTs),
      failureList: failureList.sort((a, b) => b.lastTs - a.lastTs),
    };
  }
  return result;
}

function SurfaceToggle({ surface, onChange }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
      {['user', 'checkout'].map(s => (
        <button key={s} onClick={() => onChange(s)}
          style={{
            padding: '6px 14px', fontSize: 12, fontWeight: 600, border: 'none',
            background: surface === s ? 'var(--navy)' : '#fff',
            color: surface === s ? '#fff' : 'var(--gray-700)', cursor: 'pointer',
          }}>
          {s === 'user' ? 'User' : 'Checkout'}
        </button>
      ))}
    </div>
  );
}

function UserDashboardView({
  user, now,
  selectedSessionId, selectedSessionEvents, selectedLoading,
  expandedRows, setExpandedRows,
  aiSummary, aiLoading, aiError, eventsExpanded, setEventsExpanded,
  onSelectSession, onClearSelection,
}) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
        {USER_FUNNELS.map(f => (
          <FunnelColumn key={f}
            data={user[f]}
            now={now}
            selectedSessionId={selectedSessionId}
            onSelect={onSelectSession} />
        ))}
      </div>

      {selectedSessionId && (
        <div style={{ ...CARD }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--gray-50)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>Selected session</div>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2, fontFamily: '"SF Mono", Menlo, monospace' }}>
                {selectedSessionId}{selectedLoading && ' · Loading events…'}
                {!selectedLoading && selectedSessionEvents.length > 0 && ` · ${selectedSessionEvents.length} events`}
              </div>
            </div>
            <button onClick={onClearSelection} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--gray-500)' }}>✕</button>
          </div>
          <AiSummaryCard summary={aiSummary} loading={aiLoading} error={aiError} eventCount={selectedSessionEvents.length} />
          {selectedSessionEvents.length > 0 && (
            <div style={{ borderTop: '1px solid var(--gray-200)' }}>
              <button onClick={() => setEventsExpanded(v => !v)}
                style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', fontFamily: 'inherit' }}>
                {eventsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {eventsExpanded ? 'Hide' : 'Show'} raw event log ({selectedSessionEvents.length} events)
              </button>
              {eventsExpanded && (
                <div style={{ maxHeight: 480, overflowY: 'auto', borderTop: '1px solid var(--gray-100)' }}>
                  {selectedSessionEvents.map((e, i) => (
                    <EventRow key={e._id || i} event={e}
                      expanded={!!expandedRows[e._id || i]}
                      onToggle={() => setExpandedRows(p => ({ ...p, [e._id || i]: !p[e._id || i] }))}
                      onSelect={() => null} selected={false} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function FunnelColumn({ data, now, selectedSessionId, onSelect }) {
  const successRatePct = data.successRate == null ? '—' : `${(data.successRate * 100).toFixed(0)}%`;
  const successRateColor =
      data.successRate == null  ? 'muted'
    : data.successRate >= 0.85  ? 'green'
    : data.successRate >= 0.70  ? 'yellow'
    :                              'red';
  const c = TILE_COLORS[successRateColor];

  return (
    <div style={{ ...CARD, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* Header */}
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--gray-200)', background: 'var(--gray-50)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{data.label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--gray-700)', marginTop: 4, fontVariantNumeric: 'tabular-nums', lineHeight: 1.5 }}>
          <div><strong>{data.attempted}</strong> attempted</div>
          <div style={{ color: c.fg }}>
            <strong>{data.succeeded}</strong> succeeded ({successRatePct})
          </div>
          <div>
            <strong>{data.failed}</strong> failed{' · '}
            <strong style={{ color: data.stuck > 0 ? '#a06000' : 'inherit' }}>{data.stuck}</strong> stuck
          </div>
        </div>
      </div>

      {/* Surface breakdown */}
      <div style={{ padding: '8px 14px', borderBottom: '1px solid var(--gray-100)' }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Surfaces</div>
        {data.surfaceCounts.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--gray-400)', fontStyle: 'italic' }}>none</div>
        ) : (
          data.surfaceCounts.map(([surface, count]) => (
            <div key={surface} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontVariantNumeric: 'tabular-nums', padding: '1px 0' }}>
              <span style={{ fontFamily: '"SF Mono", Menlo, monospace', color: 'var(--gray-700)' }}>{surface}</span>
              <span style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{count}</span>
            </div>
          ))
        )}
      </div>

      {/* Stuck list */}
      <div style={{ borderBottom: '1px solid var(--gray-100)' }}>
        <div style={{ padding: '8px 14px 2px', fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Stuck (90s+ no terminal)
        </div>
        {data.stuckList.length === 0 ? (
          <div style={{ padding: '2px 14px 8px', fontSize: 11, color: 'var(--gray-400)', fontStyle: 'italic' }}>none</div>
        ) : (
          data.stuckList.map(row => (
            <FunnelRow key={row.sessionId} row={row} now={now} kind="stuck"
              selected={selectedSessionId === row.sessionId} onSelect={onSelect} />
          ))
        )}
      </div>

      {/* Failures list */}
      <div>
        <div style={{ padding: '8px 14px 2px', fontSize: 9.5, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Recent failures
        </div>
        {data.failureList.length === 0 ? (
          <div style={{ padding: '2px 14px 8px', fontSize: 11, color: 'var(--gray-400)', fontStyle: 'italic' }}>none</div>
        ) : (
          data.failureList.slice(0, 10).map(row => (
            <FunnelRow key={row.sessionId} row={row} now={now} kind="failure"
              selected={selectedSessionId === row.sessionId} onSelect={onSelect} />
          ))
        )}
      </div>
    </div>
  );
}

function FunnelRow({ row, now, kind, selected, onSelect }) {
  const c = TILE_COLORS[kind === 'stuck' ? 'yellow' : 'red'];
  return (
    <div onClick={() => onSelect(row.sessionId)} style={{
      padding: '6px 14px',
      cursor: 'pointer',
      background: selected ? '#e6f1fa' : 'transparent',
      borderBottom: '1px solid var(--gray-50)',
    }}>
      <div style={{ fontSize: 10.5, color: 'var(--gray-500)', fontFamily: '"SF Mono", Menlo, monospace' }}>
        {fmtAgo(row.lastTs, now)}{row.surface ? ` · ${row.surface}` : ''}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--gray-800)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {row.customer || `sess-${row.sessionId.slice(-8)}`}
      </div>
      {kind === 'failure' && row.errorMessage && (
        <div style={{ fontSize: 10.5, color: c.fg, marginTop: 1, fontFamily: '"SF Mono", Menlo, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {String(row.errorMessage).slice(0, 60)}
        </div>
      )}
    </div>
  );
}
